import {
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery,
    useReactiveVar,
} from '@apollo/client'
import {
    AuthContext,
    IAuthContext,
} from '@deltek/specification-client/core/Configurations/AuthContext'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import {
    ListingMatch,
    LogEventSource,
} from '@deltek/specification-client/core/Models/listingSearch'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import {
    AddManufacturerProductListingElementResult,
    ProjectElementNode,
} from '@deltek/specification-client/core/Models/productListingDetail'
import {
    ProductTypePropertiesInputType,
    ProductTypeSelectionInput,
} from '@deltek/specification-client/core/Models/productType'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import {
    CreateProjectManufacturerProductListingInput,
    DeleteProjectManufacturerProductListingInput,
    GetProjectManufacturerProductListingInput,
} from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import { SearchFacet } from '@deltek/specification-client/core/Models/searchFacet'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
    CreateProductFlagForReviewMutation,
    RemoveProductFlagForReviewMutation,
} from 'api/graphQL/mutations/product.mutations'
import {
    AddManufacturerProductListingElementsMutation,
    AddManufacturerProductListingInProjectMutation,
    DeleteProjectManufacturerProductListingMutation,
    SetProjectManufacturerProductListingAsBODMutation,
    ToggleMultipleManufacturerSelectionsMutation,
} from 'api/graphQL/mutations/projectNugget.mutations'
import {
    GetProductFlagForReviewQuery,
    ProductCardAutosuggestQuery,
    ProductSearchAutosuggestQuery,
    ProductSearchQuery,
    RelatedProductsQuery,
} from 'api/graphQL/queries/product.queries'
import { GetProjectQuery } from 'api/graphQL/queries/project.queries'
import {
    GetNuggetForProductListingsQuery,
    GetProductTypesWithBODQuery,
    GetProjectElementNodesQuery,
    GetProjectManufacturerProductListingQuery,
    GetProjectNuggetForProductListingsQuery,
} from 'api/graphQL/queries/projectElements.queries'
import {
    setUser,
    userInfo,
    userInfoInitial,
} from 'api/graphQL/reactiveVars/user'
import { isOmWorkspaceUser } from 'api/graphQL/reactiveVars/workspaceContent'
import {
    AutoSuggestFieldType,
    CreateFlagForReviewResponse,
    GetFlagForReviewResponse,
    ProductCardAutosuggestResponse,
    ProductListingFlagForReview,
    ProductSearchAutosuggestResponse,
    ProductSearchResponse,
    RemoveFlaggedForReviewResponse,
    SearchFacetInput,
    SearchFacets,
    SelectedManufacturerListing,
} from 'api/graphQL/types/product.type'
import { UpdatedListingSearchInput } from 'api/graphQL/types/project.types'
import {
    GetNuggetResponse,
    GetProductTypesWithBODResponse,
    GetProjectElementNodesResponse,
    GetProjectManufacturerProductListingResponse,
    GetProjectNuggetResponseForImportedNugget,
    ProductTypeWithBODItem,
} from 'api/graphQL/types/projectElement.types'
import { processEnv } from 'components/configuration/environmentVariables'
import debounce from 'lodash.debounce'
import isEqual from 'lodash.isequal'
import { AllProjectResponse, FacetItem } from 'Models/project'
import { AreaContext } from 'pages/ProjectsPage/WorkspacePage/components/Common/AreaContext'
import useBetaWorkspaceToggle from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceRightPane/hooks/useBetaWorkspaceToggle'
import React, { FC, useCallback, useContext, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { FixedSizeGrid } from 'react-window'
import { ProductListings } from 'shared/browserTitle'
import {
    advancedFilterHRColor,
    listingDialogFilterContainerColor,
} from 'shared/common/productListingDialogColors'
import { combineAndSortManufacturers } from 'shared/utils/manufacturerListUtils'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import {
    getDialogUrl,
    getFromLocalStorage,
    getProductTypeSelectionsData,
    removeDuplicateProductTypeFacets,
    replaceSpecAgentIdForProductTypes,
    unescapeElementDelimiter,
    updatePersistentConfigs,
    WORKSPACE_ENABLED_PTP_OPTIONS,
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WORKSPACE_MANUFACTURERS_CONTENT_ITEMS_KEY,
    WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY,
    WORKSPACE_PRODUCT_LISTING_ACTION,
    WORKSPACE_PRODUCT_LISTING_KEY,
    WORKSPACE_TREE_ELEMENT_NODES,
    WorkspaceAddSelectedProductCount,
    WorkspaceManufacturerContentItems,
    WorkspaceManufacturerListingItem,
    WorkspaceManufacturerSelected,
    WorkspaceProductListing,
    WorkspaceTreeElementNodes,
} from 'shared/utils/workspaceUtils'
import AdvancedFilters from './AdvancedFilters'
import { DialogContent } from './DialogContent'
import { DialogProvider } from './DialogContext'
import { DialogFilterProvider } from './DialogFilterContext'
import { RelatedProductsBanner } from './Footer/RelatedProductsBanner'
import { DialogAppBar } from './Header/DialogAppBar'
import { DialogHeader } from './Header/DialogHeader'
import RemoveProductWarning from './Prompts/RemoveProductWarning'
import { SlideContent } from './SlideContent'
import { DialogTabs } from './Tabs/DialogTabs'

export const PER_PAGE = 50
export const OPTIMISTIC_CARDS = 5

export type SelectionType = 'BOD' | 'Product'
export type PartnershipType = 'BIM Resources' | 'Sustainability Resources'
export type DisplayTextFormatType =
    | 'ManufacturerAndProduct'
    | 'Manufacturer'
    | 'None'

export type ProductListingDialogFacet =
    | 'Categories'
    | 'SectionNames'
    | 'ListingLevels'
    | 'ProductType'
    | 'FirmName'
    | 'Sustainability Resources'
    | 'BIM Resources'
    | 'partnerships'

export enum ListingDisplayTextFormat {
    ManufacturerAndProduct = 'MANUFACTURERANDPRODUCT',
    Manufacturer = 'MANUFACTURER',
    None = 'NONE',
}

export enum PtpOptionsToInclude {
    MasterSpecAndManufacturerOptions = 'MASTERSPECANDMANUFACTUREROPTIONS',
    MasterSpecOptions = 'MASTERSPECOPTIONS',
    NoOptions = 'NOOPTIONS',
}

const useStyles = makeStyles((theme: Theme) => ({
    backdrop: {
        zIndex: theme.zIndex?.drawer + 1,
        color: theme.palette?.common.white,
    },
}))
export interface ProductListingSelected extends Partial<ListingMatch> {
    selectionType: SelectionType
    isBOD: boolean
    elementId: string
    hasSupportedManufacturersOptions: boolean
}

const areBadgelessCardsEnabled =
    processEnv.REACT_APP_EnableBadgelessCards === '1'

const defaultListingOrder = `type${areBadgelessCardsEnabled ? ' desc' : ''
    },firmName,productType,productName,lastUpdated desc`

const defaultProductTypeSelections = (
    hasFamily: boolean,
    family: string,
    hasProductType: boolean,
    productType: string,
    ptpOptions?: ProductTypePropertiesInputType[]
) => {
    if (hasFamily && hasProductType) {
        return [
            {
                sectionName: family,
                productTypeName: productType,
                productTypeProperties: ptpOptions?.map((ptpOption) => ({
                    propertyName: ptpOption.propertyName.toString(),
                    propertyOptionIds: ptpOption.propertyOptionIds.map(
                        (property) => property.toString().normalize('NFKC')
                    ),
                })),
            },
        ]
    }
    return []
}

export enum productOptions {
    ADDED_TO_PROJECT = 'Added to Project',
    FLAGGED_FOR_REVIEW = 'Flagged for Review',
}

export const defaultProduct = {
    [productOptions.ADDED_TO_PROJECT]: false,
    [productOptions.FLAGGED_FOR_REVIEW]: false,
}

const sustainabilityResourcesOptions = ['Sustainable Minds']
const bimResourcesOptions = ['BIMsmith']
const defaultFilter: string[] = !areBadgelessCardsEnabled
    ? ['Premium MasterSpec', 'Premium']
    : ['Product Listing']

const ProductListingDialog: FC = () => {
    const { setHeaderVisibility, updateBrowserTitle } = React.useContext(
        AppBarContext
    )
    const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()

    const { selectedProjectNode } = React.useContext(AreaContext)

    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const serviceWorker = navigator.serviceWorker

    const [closeSlide, setCloseSlide] = React.useState<boolean>(false)

    const history = useHistory()
    const location = useLocation()
    const classes = useStyles()

    const userDetails = useReactiveVar(userInfo)

    const [productsPage, setProductsPage] = React.useState<boolean>(false)

    const [isAEAllProductsToggled, setIsAEAllProductsToggled] = React.useState<
        boolean
    >(false)

    const { projectId } = useParams<{ projectId?: string }>()

    const currentUserInfoResponse = useContext(AuthContext) as IAuthContext

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const userId = React.useMemo(
        () => currentUserInfoResponse?.currentUserInfo.kpUserId,
        [currentUserInfoResponse]
    )

    const [searchLoading, setSearchLoading] = React.useState<boolean>(false)
    const [searchFetchMoreLoading, setSearchFetchMoreLoading] = React.useState<
        boolean
    >(false)
    const [currentTotal, setCurrentTotal] = React.useState<number>(PER_PAGE)
    const [currentRTotal, setCurrentRTotal] = React.useState<number>(PER_PAGE)
    const [refetch, setRefetch] = React.useState<boolean>(false)
    const [hasNextPage, setHasNextPage] = React.useState<boolean>(true)
    const [rHasNextPage, setRHasNextPage] = React.useState<boolean>(true)
    const [scrollState, setScrollState] = React.useState({
        rowIndex: 0,
        columnIndex: 0,
    })
    const [columnCount, setColumnCount] = React.useState<number>(PER_PAGE)
    const hasMountedRef = React.useRef(false)
    const infiniteLoaderRef = React.useRef(null)
    // Track dialog-initiated additions to send service worker message after mutation (only used when beta toggle is enabled)
    const dialogAdditionDataRef = React.useRef<any>(null)
    const [reScroll, setReScroll] = React.useState<boolean>(false)

    const queryParams = new URLSearchParams(useLocation().search)
    const undecodedCategory = queryParams.get('category')
    const elementNode = queryParams.get('elementNode')
    const isInProject = queryParams.get('isInProject') === 'true'
    const category =
        undecodedCategory &&
        decodeURIComponent(
            unescapeElementDelimiter(undecodedCategory).split('||').join(' ')
        )
    const undecodedFamily = queryParams.get('family')
    const divisionNumber = queryParams.get('divisionNumber')
    const isForDivisionView =
        queryParams.get('selectedFilterType') === 'DIVISIONS'
    const family =
        undecodedFamily &&
        decodeURIComponent(
            unescapeElementDelimiter(undecodedFamily).split('||').join(' ')
        )
    const previousFamily = usePrevious(family)
    const undecodedProductType = queryParams.get('productType')
    const productType =
        undecodedProductType &&
        decodeURIComponent(
            unescapeElementDelimiter(undecodedProductType).split('||').join(' ')
        )

    const previousProductType = usePrevious(productType)
    const parentNodeId = queryParams.get('parentNodeId')
    const undecodedAssemblyPath = queryParams.get('assemblyPath')
    const assemblyPath =
        undecodedAssemblyPath &&
        decodeURIComponent(
            unescapeElementDelimiter(undecodedAssemblyPath)
                .split('#')
                .join(' ')
                .split('||')
                .join(' / ')
                .split('|')
                .join(' ')
        )

    const previousAssemblyPath = usePrevious(assemblyPath)
    const specAgentIdPt = queryParams.get('specAgentIdPt')
    const nodeId = queryParams.get('nodeId')
    const previousNodeId = usePrevious(nodeId)
    const elementId = queryParams.get('elementId')
    const elementType = queryParams.get('elementType')
    const isLastSubAssembly =
        queryParams.get('isLastSubAssembly') === 'true' ? true : false
    const isFromRevit = queryParams.get('source') === 'plugin' ? true : false

    const isAssembly = elementType === 'ASSEMBLY' && !isLastSubAssembly
    const isProductType = elementType === 'PRODUCTTYPE'
    const isProductFamily = elementType === 'PRODUCTFAMILY'
    const hasFamily = !!elementType && (isProductType || isProductFamily)

    const hasProductType = !!elementType && isProductType

    const isGettingSelectedNuggetRequired =
        isProductType && projectId && elementId

    const listingId = queryParams.get('listingId')

    const categoryNodeId = queryParams.get('categoryNodeId')
    const baseElementId = queryParams.get('baseElementId')
    const treePath = queryParams.get('treePath')
        ? decodeURIComponent(queryParams.get('treePath')!)
        : undefined

    // Determine if this is a division view based on tree path
    const isDivisionViewFromTreePath = React.useMemo(() => {
        if (!treePath) return false

        // Split the tree path by || to get path segments
        const pathSegments = treePath.split('||')
        if (pathSegments.length === 0) return false

        // Get the first segment and split by | to get the identifier
        const firstSegment = pathSegments[0]
        const firstSegmentParts = firstSegment.split('|')
        if (firstSegmentParts.length === 0) return false

        const firstIdentifier = firstSegmentParts[0].trim()
        // Division paths start with 2-digit division numbers (e.g., "03")
        // Assembly paths start with "A" followed by numbers (e.g., "A1020")
        const isDivisionFormat = /^\d{2}$/.test(firstIdentifier)
        const isAssemblyFormat = /^A\d+/.test(firstIdentifier)

        // Return true only if it matches division format and NOT assembly format
        return isDivisionFormat && !isAssemblyFormat
    }, [treePath])

    // Filters
    const [listingType, setListingType] = React.useState<string[]>([])
    const [product, setProduct] = React.useState<Record<string, boolean>>(
        defaultProduct
    )
    const [productSelected, setProductSelected] = React.useState<
        ProductListingSelected[]
    >([])
    const [
        productListingFlaggedForReview,
        setProductListingFlaggedForReview,
    ] = React.useState<ProductListingFlagForReview[]>([])
    const [specialty, setSpecialty] = useState<string[]>([])
    const [bimResources, setBIMResources] = React.useState<string[]>([])
    const [sustainabiliyResources, setSustainabilityResources] = React.useState<
        string[]
    >([])

    const [isRemoveWarningOpen, setIsRemoveWarningOpen] = React.useState<
        boolean
    >(false)

    const [listingInfoToDelete, setListingInfoToDelete] = React.useState<
        { listingId: string; isBOD: boolean } | undefined
    >()

    const defaultManufacturerProductSearchInput = React.useMemo(() => ({
        skip: 0,
        top: PER_PAGE,
        isOnAE: true,
        projectId: projectId,
        logEventSource: LogEventSource.AeViewProducts,
        productTypeSelections: [], // this field cannot be null. If it is null, the api will throw error
    }), [projectId])

    const [
        manufacturerProductSearchInput,
        setManufacturerProductSearchInput,
    ] = React.useState<Partial<UpdatedListingSearchInput>>(
        defaultManufacturerProductSearchInput
    )

    const [searchFacets, setSearchFacets] = React.useState<
        Partial<SearchFacet>[]
    >([])

    const [productTypesWithBOD, setProductTypesWithBOD] = React.useState<
        ProductTypeWithBODItem[]
    >([])

    const [autosuggestFacet, setAutosuggestFacet] = React.useState<{
        name?: ProductListingDialogFacet
        value?: string
    }>({})

    const [autosuggestSaved, setAutosuggestSaved] = React.useState<boolean>(
        true
    )
    const [autosuggestCardSaved, setAutosuggestCardSaved] = React.useState<
        boolean
    >(true)
    const [
        isProductTypeFacetsLoading,
        setIsProductTypeFacetsLoading,
    ] = React.useState<boolean>(
        location.pathname.includes('products') ? false : !isAssembly
    )

    const [isAddElementsLoading, setIsAddElementsLoading] = React.useState<
        boolean
    >(false)

    const [
        isFlagForReviewInProgress,
        setIsFlagForReviewInProgress,
    ] = React.useState<boolean>(false)

    const [isBannerExpanded, setIsBannerExpanded] = React.useState<boolean>(
        false
    )

    const productSelectedListingIds = React.useMemo(
        () => productSelected.map((each) => each.listingId as string) ?? [],
        [productSelected]
    )

    const flaggedForReviewListingIds = React.useMemo(
        () =>
            productListingFlaggedForReview.map(
                (each) => each.listingId as string
            ) ?? [],
        [productListingFlaggedForReview]
    )

    const calculateEmptyListings = React.useCallback(() => {
        const addedToProject = product[productOptions.ADDED_TO_PROJECT]
        const flaggedForReview = product[productOptions.FLAGGED_FOR_REVIEW]
        const noProductListingFlagged = !productListingFlaggedForReview.length
        const noAddedToProjectListingIds = !productSelectedListingIds.length

        let isEmpty = false

        if (addedToProject) {
            isEmpty =
                noAddedToProjectListingIds &&
                (!flaggedForReview ||
                    (!!flaggedForReview && noProductListingFlagged))
        } else if (flaggedForReview && noProductListingFlagged) {
            isEmpty = true
        }

        return isEmpty
    }, [product, productSelectedListingIds, productListingFlaggedForReview])

    const isEmptyListingDueToFilter = React.useMemo(() => {
        return calculateEmptyListings()
    }, [calculateEmptyListings])

    const defaultSearchFacetInputs = React.useCallback(() => {
        let facetInputs: SearchFacetInput[] = [
            { name: 'isActive', values: ['true'] },
        ]
        if (hasFamily) {
            facetInputs = [
                ...facetInputs,
                { name: 'SectionNames', values: [family as string] },
            ]
        }
        if (category && category !== 'All Categories') {
            facetInputs = [
                ...facetInputs,
                { name: 'categories', values: [category] },
            ]
        }
        if (
            !isProductFamily &&
            !isProductType &&
            (isAssembly || isLastSubAssembly)
        ) {
            facetInputs = [
                ...facetInputs,
                { name: 'assemblyNodeId', values: [nodeId as string] },
            ]
        }
        return facetInputs
    }, [
        category,
        hasFamily,
        family,
        isProductFamily,
        isProductType,
        isAssembly,
        isLastSubAssembly,
        nodeId,
    ])

    const [searchFacetInputs, setSearchFacetInputs] = React.useState<
        SearchFacetInput[]
    >(defaultSearchFacetInputs())

    const [productTypeSelections, setProductTypeSelections] = React.useState<
        Partial<ProductTypeSelectionInput>[]
    >()

    const defaultProductTypeFacets = useMemo(() => {
        return [
            {
                value: productType ?? '',
                specAgentProductTypeId: specAgentIdPt,
                parentProductFamily: family!,
                pTElementId: elementId,
                pTNodeId: nodeId,
                familyElementId: elementId,
                familyNodeId: nodeId,
            },
        ]
    }, [productType, specAgentIdPt, family, elementId, nodeId])

    const [productTypeFacets, setProductTypeFacets] = React.useState<
        Partial<SearchFacets>[]
    >(defaultProductTypeFacets)

    const [propertyOptions, setPropertyOptions] = React.useState<
        ProductTypePropertiesInputType[]
    >([])
    const [orderBy, setOrderBy] = React.useState<string>()
    const [dialogTab, setDialogTab] = React.useState(0)
    const previousDialogTab = usePrevious(dialogTab)
    const productTypeWithBODForDivisionInput = React.useMemo(
        () => ({
            projectId,
            productFamilyGroupId: nodeId,
        }),
        [projectId, nodeId]
    )
    const isDivisionSelected = React.useMemo(
        () => !!divisionNumber && !!isForDivisionView,
        [divisionNumber, isForDivisionView]
    )

    const [
        projectProductListingCount,
        setProjectProductListingCount,
    ] = React.useState<number | undefined>()

    const [selectedManufacturers, setSelectedManufacturers] = React.useState<
        SelectedManufacturerListing[]
    >([])

    const handleMultiSelectManufacturer = React.useCallback(
        (listingId: string, listingLevel: ListingLevel, firmName: string) => {
            if (listingLevel !== ListingLevel.Manufacturer) {
                return
            }

            // Don't add manufacturers that are already added to the project
            if (productSelectedListingIds.includes(listingId)) {
                return
            }

            const safeSelectedManufacturers = selectedManufacturers ?? []
            const found = safeSelectedManufacturers.find(
                (item: SelectedManufacturerListing) =>
                    item.listingId === listingId
            )
            if (found) {
                // Remove listingId if already present
                setSelectedManufacturers(
                    safeSelectedManufacturers.filter(
                        (item: SelectedManufacturerListing) =>
                            item.listingId !== listingId
                    )
                )
            } else {
                // Add listingId if not present, then sort by firmName
                const newList = [
                    ...safeSelectedManufacturers,
                    {
                        listingId,
                        firmName,
                        type: listingLevel.split(' ').join('').toUpperCase(),
                    } as SelectedManufacturerListing,
                ].sort((a, b) => a.firmName.localeCompare(b.firmName))
                setSelectedManufacturers(newList)
            }
        },
        [selectedManufacturers, setSelectedManufacturers, productSelectedListingIds]
    )

    // Reusable callback to send service worker message with product count
    const sendServiceWorkerMessage = React.useCallback(async (selectedProductsCount: number) => {
        const message = {
            [WORKSPACE_PRODUCT_LISTING_ACTION]: {
                selectedProducts: selectedProductsCount,
            } as WorkspaceAddSelectedProductCount,
        }

        if (serviceWorker?.controller) {
            serviceWorker.controller.postMessage(message)
        } else if (serviceWorker) {
            try {
                const registration = await serviceWorker.ready
                if (registration.active) {
                    registration.active.postMessage(message)
                }
            } catch (error) {
                console.warn('Service worker not available for message passing:', error)
            }
        } else {
            console.warn('Service worker not available')
        }
    }, [serviceWorker])

    const [getSelectedProducts] = useLazyQuery<
        GetProjectManufacturerProductListingResponse
    >(GetProjectManufacturerProductListingQuery, {
        displayName: 'ProjectProductListing',
        fetchPolicy: 'no-cache',
        variables: {
            input: {
                projectId,
                projectElementType: ProjectElementType.PRODUCTTYPE, // can be PRODUCTFAMILY, ASSEMBLIES, etc\
            } as GetProjectManufacturerProductListingInput,
        },
        onCompleted: (data) => {
            const projectManufacturerProductListing =
                data?.getProjectManufacturerProductListing || []

            setProductSelected(
                projectManufacturerProductListing.map((each) => ({
                    listingId: each.listingId as string,
                    selectionType: each.isBOD ? 'BOD' : 'Product',
                    isBOD: each.isBOD ?? false,
                    isActive: each.isBOD ?? false,
                    type: each.type as ListingLevel,
                    firmName: each.firmName as string,
                    hasSupportedManufacturersOptions:
                        each.hasSupportedManufacturersOptions ?? false,
                    productType: each.productType,
                    elementId: each.elementId as string,
                })) ?? []
            )
            setProjectProductListingCount(
                projectManufacturerProductListing.length
            )

            getProductFlaggedForReviewProducts({
                variables: {
                    projectId: projectId,
                },
            })

            // Send service worker message with updated count
            sendServiceWorkerMessage(projectManufacturerProductListing.length)

        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on fetching project product listing',
                    variant: 'error',
                    id: `ProductListingsPage_ProjectProductListingError`,
                },
            })
        },
    })
    const [
        getListings,
        {
            data: searchData,
            fetchMore: searchFetchMore,
            called: searchCalled,
            loading: getListingsLoading,
        },
    ] = useLazyQuery<ProductSearchResponse>(ProductSearchQuery, {
        displayName: 'ProductSearchResult',
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
        onCompleted: (data) => {
            if (data.listingSearch.facets) {
                if (!productsPage && !isAEAllProductsToggled) {
                    // removing SectionNames and ProductType from new Facets
                    const newSearchFacets = data.listingSearch.facets.filter(
                        (item) =>
                            item.name !== 'SectionNames' &&
                            item.name !== 'ProductType' &&
                            item.name !== 'FirmName'
                    )
                    // Fetch SectionNames and ProductType from old Facets
                    const oldSearchFacets = [...searchFacets].filter(
                        (item) =>
                            item.name === 'SectionNames' ||
                            item.name === 'ProductType' ||
                            item.name === 'FirmName'
                    )
                    setSearchFacets([...newSearchFacets, ...oldSearchFacets])
                } else {
                    setSearchFacets(data.listingSearch.facets)
                }
                getMultiplePTsWithBOD()
                setSearchLoading(false)
                setIsAddElementsLoading(false)
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product search',
                    variant: 'error',
                    id: `ProductListingsPage_ProductSearchError`,
                },
            })
            setSearchLoading(false)
            setIsAddElementsLoading(false)
        },
    })

    const [
        getRelatedProducts,
        {
            data: relatedProducts,
            called: relatedProductsCalled,
            fetchMore: getMoreRelatedProducts,
            loading: getrelatedProductsLoading,
        },
    ] = useLazyQuery<ProductSearchResponse>(RelatedProductsQuery, {
        displayName: 'RelatedProductsQuery',
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error retrieving related products',
                    variant: 'error',
                    id: `ProductListingsPage_RelatedProductsError`,
                },
            })
            setSearchLoading(false)
        },
    })

    const [
        getListingsAutosuggest,
        { loading: autosuggestLoading, data: autosuggestData },
    ] = useLazyQuery<ProductSearchAutosuggestResponse>(
        ProductSearchAutosuggestQuery,
        {
            displayName: 'ProductSearchAutoSuggestResult',
            fetchPolicy: 'no-cache',
            variables: {
                input: {
                    projectId,
                    projectElementType: ProjectElementType.PRODUCTTYPE, // can be PRODUCTFAMILY, ASSEMBLIES, etc\
                } as GetProjectManufacturerProductListingInput,
            },
            onCompleted: () => {
                setAutosuggestSaved(false)
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on product autosuggest in dialog',
                        variant: 'error',
                        id: `ProductListingsDialog_ProductAutosuggestError`,
                    },
                })
            },
        }
    )
    const [
        getListingCardAutosuggest,
        { loading: autosuggestCardLoading, data: autosuggestCardData },
    ] = useLazyQuery<ProductCardAutosuggestResponse>(
        ProductCardAutosuggestQuery,
        {
            displayName: 'ProductCardAutoSuggestResult',
            fetchPolicy: 'no-cache',
            onCompleted: () => {
                setAutosuggestCardSaved(false)
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on product autosuggest in dialog',
                        variant: 'error',
                        id: `ProductListingsDialog_ProductAutosuggestError`,
                    },
                })
            },
        }
    )
    const [
        getProject,
        { data: projectData, loading: projectLoading, called: projectCalled },
    ] = useLazyQuery<AllProjectResponse>(GetProjectQuery, {
        displayName: 'ProjectDetail',
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'ProductListingsPage_ProjectDetailError',
                },
            })
        },
    })

    const {
        data: projectProductListingData,
        loading: projectProductListingLoading,
    } = useQuery<GetProjectManufacturerProductListingResponse>(
        GetProjectManufacturerProductListingQuery,
        {
            displayName: 'ProjectProductListing',
            fetchPolicy: 'no-cache',
            variables: {
                input: {
                    projectId,
                    projectElementType: isProductType
                        ? ProjectElementType.PRODUCTTYPE
                        : isProductFamily
                            ? ProjectElementType.PRODUCTFAMILY
                            : ProjectElementType.ASSEMBLY, // can be PRODUCTFAMILY, ASSEMBLIES, etc\
                } as GetProjectManufacturerProductListingInput,
            },
            skip: !projectId,
            onCompleted: () => {
                getProductFlaggedForReviewProducts({
                    variables: {
                        projectId: projectId,
                    },
                })
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on fetching project product listing',
                        variant: 'error',
                        id: `ProductListingsPage_ProjectProductListingError`,
                    },
                })
            },
        }
    )

    const wasListingsLoading = usePrevious(projectProductListingLoading)

    // Ensures atleast one variable exist before calling getProductTypesWithBod
    const onGetProductTypesWithBod = React.useCallback(
        (nodeIds: string[] | undefined) => {
            if (isDivisionSelected && !!productTypeWithBODForDivisionInput) {
                // From Division view with valid variables
                getProductTypesWithBod({
                    variables: {
                        nodeIds: undefined,
                        input: productTypeWithBODForDivisionInput,
                    },
                })
            } else if (
                !isDivisionSelected &&
                !!nodeIds?.length &&
                nodeIds[0] !== 'undefined'
            ) {
                // From Assembly view with valid variables
                getProductTypesWithBod({
                    variables: {
                        nodeIds: nodeIds,
                        input: undefined,
                    },
                })
            }
        },
        [isDivisionSelected, productTypeWithBODForDivisionInput]
    )

    const [getProductTypesWithBod] = useLazyQuery<
        GetProductTypesWithBODResponse
    >(GetProductTypesWithBODQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'getProductTypesWithBOD',
        onCompleted: (data) => {
            if (data) {
                setProductTypesWithBOD(
                    data?.getProductTypesWithBod as ProductTypeWithBODItem[]
                )
            }
        },
        onError: (err) => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on fetching product types with BOD',
                    variant: 'error',
                    id: `ProductListingsPage_ProjectTypeWithBODError`,
                },
            })
        },
    })

    const [
        getSelectedNugget,
        { data: projectNuggetData, loading: projectNUggetLoading },
    ] = useLazyQuery<GetProjectNuggetResponseForImportedNugget>(
        GetProjectNuggetForProductListingsQuery(6),
        {
            fetchPolicy: 'no-cache',
            displayName: 'GetProjectNugget',
            onCompleted: (data) => {
                if (!data) {
                    showGlobalSnackbar({
                        variables: {
                            message:
                                'Product Type for the selected Product Listing does not exist',
                            variant: 'error',
                            id: `ProductListingsPage_ProjectNuggetError`,
                        },
                    })
                }
            },
            onError: () => {
                // removed error message because, the elementId could be a baseElementId
                // let's try to query for the base nugget
                getNugget({
                    variables: {
                        id: isProductType ? elementId : '',
                        nodeId: nodeId,
                    },
                })
            },
        }
    )

    const [getNugget] = useLazyQuery<GetNuggetResponse>(
        GetNuggetForProductListingsQuery,
        {
            fetchPolicy: 'no-cache',
            displayName: 'Nugget',
            onCompleted: (data) => {
                if (!data) {
                    showGlobalSnackbar({
                        variables: {
                            message:
                                'Product Type for the selected Product Listing does not exist',
                            variant: 'error',
                            id: `ProductListingsPage_ProjectNuggetError`,
                        },
                    })
                }
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on fetching nugget',
                        variant: 'error',
                        id: `ProductListingsPage_ProjectNuggetError`,
                    },
                })
            },
        }
    )

    // Fetch product listing flagged for review
    const [getProductFlaggedForReviewProducts] = useLazyQuery<
        GetFlagForReviewResponse
    >(GetProductFlagForReviewQuery, {
        displayName: 'ProductListingFlagForReview',
        fetchPolicy: 'no-cache',
        variables: {
            projectId: projectId,
        },
        onCompleted: (data: GetFlagForReviewResponse) => {
            setProductListingFlaggedForReview(
                data?.getProductListingsFlaggedForReview?.items || []
            )
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message:
                        'Error on fetching product listing flagged for review',
                    variant: 'error',
                    id: `ProductListingsPage_ProductListingFlaggedForReviewError`,
                },
            })
        },
    })

    const [addListingElements] = useMutation<{
        addManufacturerProductListingElements: AddManufacturerProductListingElementResult
        updatedInput: Partial<CreateProjectManufacturerProductListingInput>
        projectElementNodes: Partial<ProjectElementNode[]>
        createProjectManufacturerProductListingInput: Partial<
            CreateProjectManufacturerProductListingInput
        >
    }>(AddManufacturerProductListingElementsMutation, {
        onCompleted: (data) => {
            if (data?.addManufacturerProductListingElements.updatedInput) {
                const projectElementNodes =
                    data?.addManufacturerProductListingElements
                        .projectElementNodes ?? []
                // call add listing mutation
                const {
                    listingId,
                    projectId,
                    __typename,
                    ...input
                }: any = data?.addManufacturerProductListingElements?.updatedInput

                // // IMMEDIATELY send service worker message for beta workspace
                if (
                    isBetaWorkspaceToggleEnabled &&
                    dialogAdditionDataRef.current
                ) {
                    const messageData = dialogAdditionDataRef.current

                    // Send the message with all necessary data
                    serviceWorker?.controller?.postMessage({
                        [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                            ...messageData,
                            elementId: input?.elementId, // Use the elementId from updatedInput
                            nodeId:
                                projectElementNodes?.[0]?.id ||
                                messageData.nodeId,
                            isFromDialogMutation: true,
                        } as WorkspaceManufacturerSelected,
                        // Include the full mutation response data for useServiceWorkerListener
                        addManufacturerProductListingElements:
                            data?.addManufacturerProductListingElements,
                    })

                    // Update workspace element tree with projectElementNodes
                    if (projectElementNodes && projectElementNodes.length > 0) {
                        updateWorkspaceElementTree(
                            projectElementNodes,
                            input?.elementId
                        )
                    }
                }

                // Remove flag for review if needed
                if (!!listingId && !!projectId) {
                    const hasFlagForReview = !!productListingFlaggedForReview.find(
                        (item) => item.listingId === listingId
                    )
                    if (hasFlagForReview) {
                        setIsFlagForReviewInProgress(true)
                        removeFlagForReview({
                            variables: {
                                projectId: projectId,
                                listingId: listingId,
                            },
                        }).then(() => {
                            setManufacturerProductSearchInput((past) => ({
                                ...past,
                                projectId,
                                listingIdsList: past.listingIdsList?.filter(
                                    (each) => each !== listingId
                                ),
                            }))
                            setIsFlagForReviewInProgress(false)
                        })
                    }
                }

                if (input.isBOD) {
                    addListingAsBOD({
                        variables: {
                            input: {
                                ...input,
                                listingId,
                                projectId,
                                isFromRevit,
                            },
                        },
                    }).then((value) => {
                        if (
                            isBetaWorkspaceToggleEnabled &&
                            dialogAdditionDataRef.current
                        ) {
                            dialogAdditionDataRef.current = null
                        }

                        if (
                            isProductType ||
                            searchFacetInputs?.filter(
                                (item) => item?.name === 'ProductType'
                            )?.[0]?.values?.length!! !== 0
                        ) {
                            updateWorkspaceItems(
                                value?.data
                                    ?.setProjectManufacturerProductListingAsBOD ??
                                [],
                                { ...input, listingId, projectId }
                            )
                        }
                        // For beta workspace, don't update workspace element tree
                        // as it's handled differently through the service worker
                        if (!isBetaWorkspaceToggleEnabled) {
                            // Only update workspace element tree if we have new nodes
                            // Don't send empty arrays as it might clear existing data
                            if (
                                projectElementNodes &&
                                projectElementNodes.length > 0
                            ) {
                                updateWorkspaceElementTree!(
                                    projectElementNodes,
                                    input?.elementId
                                )
                            }
                        }
                        updateProductTypeFacets('ProductType')
                        setIsAddElementsLoading(false)
                    })
                } else {
                    addListing({
                        variables: {
                            input: {
                                ...input,
                                listingId,
                                projectId,
                                isFromRevit,
                            },
                        },
                    }).then((value) => {
                        if (
                            isBetaWorkspaceToggleEnabled &&
                            dialogAdditionDataRef.current
                        ) {
                            dialogAdditionDataRef.current = null
                        }



                        const productTypeFilterLength = searchFacetInputs?.filter(
                            (item) => item?.name === 'ProductType'
                        )?.[0]?.values?.length || 0



                        if (
                            isProductType ||
                            productTypeFilterLength !== 0
                        ) {
                            // update workspace items
                            updateWorkspaceItems(
                                value?.data
                                    ?.addManufacturerProductListingInProject ??
                                [],
                                data?.addManufacturerProductListingElements
                                    ?.updatedInput
                            )
                        }
                        // For beta workspace, don't update workspace element tree
                        // as it's handled differently through the service worker
                        if (!isBetaWorkspaceToggleEnabled) {
                            // Only update workspace element tree if we have new nodes
                            // Don't send empty arrays as it might clear existing data
                            if (
                                projectElementNodes &&
                                projectElementNodes.length > 0
                            ) {
                                updateWorkspaceElementTree!(
                                    projectElementNodes,
                                    input?.elementId
                                )
                            }
                        }
                        updateProductTypeFacets('ProductType')
                        setIsAddElementsLoading(false)
                    })
                }
            }
        },
        onError: (error) => {
            setIsAddElementsLoading(false)
            setIsFlagForReviewInProgress(false)
            showGlobalSnackbar?.({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: `ProductListingDialog-AddProductListingElements`,
                },
            })
        },
    })

    // Helper function to refresh selected products and send service worker message
    const refreshSelectedProducts = React.useCallback(() => {
        getSelectedProducts()
    }, [getSelectedProducts])


    const [addListing] = useMutation<
        { addManufacturerProductListingInProject: ContentItem[] },
        { input: Partial<CreateProjectManufacturerProductListingInput> }
    >(AddManufacturerProductListingInProjectMutation, {
        onCompleted: refreshSelectedProducts,
        onError: () => {
            refreshSelectedProducts() // Ensure count is refreshed even on error
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem in adding the listing.`,
                    variant: 'error',
                    id:
                        'ProductListingsPage_AddManufacturerProductListingInProjectError',
                },
            })
        },
    })

    const [addListingAsBOD] = useMutation<
        { setProjectManufacturerProductListingAsBOD: ContentItem[] },
        { input: Partial<CreateProjectManufacturerProductListingInput> }
    >(SetProjectManufacturerProductListingAsBODMutation, {
        onCompleted: refreshSelectedProducts,
        onError: () => {
            refreshSelectedProducts() // Ensure count is refreshed even on error
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem in adding the listing.`,
                    variant: 'error',
                    id:
                        'ProductListingsPage_AddManufacturerProductListingInProjectError',
                },
            })
        },
    })

    const [deleteListing] = useMutation<
        { deleteProjectManufacturerProductListing: ContentItem[] },
        { input: Partial<DeleteProjectManufacturerProductListingInput> }
    >(DeleteProjectManufacturerProductListingMutation, {
        onError: (err) => {
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem in deleting the listing.`,
                    variant: 'error',
                    id:
                        'ProductListingsPage_DeleteProjectManufacturerProductListingError',
                },
            })
        },
    })

    const [addFlagForReview] = useMutation<CreateFlagForReviewResponse>(
        CreateProductFlagForReviewMutation,
        {
            onError: (error) => {
                setIsFlagForReviewInProgress(false)
                showGlobalSnackbar?.({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id: `ProductListingDialog-AddProductListingFlagForReview`,
                    },
                })
            },
        }
    )


    const [toggleMultipleManufacturerSelections] = useMutation(
        ToggleMultipleManufacturerSelectionsMutation,
        {
            onCompleted: (data) => {
                const manufacturersToProcess = [...selectedManufacturers]

                // Send postMessage for each selected manufacturer to update workspace nodes
                manufacturersToProcess.forEach((manufacturer) => {
                    serviceWorker?.controller?.postMessage({
                        [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                            listingId: manufacturer.listingId,
                            isSelected: true,
                            isFirstBOD: false, // Since this is for multiple selections, none are "first BOD"
                            listingText: manufacturer.firmName,
                            listingType: manufacturer.type,
                            firmName: manufacturer.firmName,
                            isBOD: false, // Multiple selections are typically for regular products, not BOD
                            hasSupportedManufacturersOptions: false, // Default value for multiple selections
                            elementId: elementId as string,
                            nodeId: nodeId,
                            isFromDialogMutation: true, // Flag to indicate this is from the multi-select mutation
                        } as WorkspaceManufacturerSelected,
                    })
                })
                setIsAddElementsLoading(false)
                setSelectedManufacturers([])
                refreshSelectedProducts()

                showGlobalSnackbar?.({
                    variables: {
                        message:
                            'Manufacturer selections updated successfully.',
                        variant: 'success',
                        id:
                            'ProductListingsDialog_ToggleMultipleManufacturerSelectionsSuccess',
                    },
                })
            },
            onError: (error) => {
                setIsAddElementsLoading(false)
                showGlobalSnackbar?.({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id:
                            'ProductListingsDialog_ToggleMultipleManufacturerSelectionsError',
                    },
                })
            },
        }
    )

    const handleToggleMultipleManufacturerSelections = React.useCallback(
        (isBOD: boolean = false, isRetainProperties: boolean = true) => {
            setIsAddElementsLoading(true)

            // Combine newly selected manufacturers with already added manufacturers
            const allSlectedManufacturers = combineAndSortManufacturers(productSelected, selectedManufacturers)

            toggleMultipleManufacturerSelections({
                variables: {
                    input: {
                        projectId: projectId,
                        projectNuggetId: projectNuggetData?.getProjectNugget.id,
                        productFamilyId: projectNuggetData?.getProjectNugget.projectProductTypePropertiesView?.productFamilyId,
                        isBOD: isBOD,
                        isRetainProperties: isRetainProperties,
                        selectedManufacturers: allSlectedManufacturers,
                    },
                },
            })
        },
        [projectId, projectNuggetData, selectedManufacturers, productSelected, toggleMultipleManufacturerSelections]
    )


    const [removeFlagForReview] = useMutation<RemoveFlaggedForReviewResponse>(
        RemoveProductFlagForReviewMutation,
        {
            onError: (error) => {
                setIsFlagForReviewInProgress(false)
                showGlobalSnackbar?.({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id: `ProductListingDialog-RemoveProductListingFlagForReview`,
                    },
                })
            },
        }
    )

    const getRelatedProductsFacets = React.useCallback(
        (facetInput: Partial<FacetItem>[]) => {
            let relatedProductsFacet = [
                ...facetInput,
                {
                    name: 'listingLevels',
                    values: [ListingLevel.PremiumProprietary],
                },
                {
                    name: 'categories',
                    values: [category],
                },
            ]
            if (isOMUser) {
                relatedProductsFacet = [
                    ...relatedProductsFacet,
                    {
                        name: 'nodeIds',
                        values: [nodeId!],
                    },
                ]
            }

            return Array.from(
                new Set(relatedProductsFacet?.map((facet) => facet.name))
            )
                ?.map((name) => {
                    if (name === 'listingLevels') {
                        if (name === 'listingLevels') {
                            return {
                                name: 'listingLevels',
                                values: [ListingLevel.PremiumProprietary],
                            }
                        }
                    } else if (name === 'categories') {
                        if (category !== 'All Categories') {
                            return {
                                ...relatedProductsFacet?.find(
                                    (facetName) => facetName?.name === name
                                ),
                            }
                        }
                    } else if (name !== 'assemblyNodeId') {
                        return {
                            ...relatedProductsFacet?.find(
                                (facetName) => facetName?.name === name
                            ),
                        }
                    }
                })
                ?.filter((x) => !!x)
        },
        [category, isOMUser]
    )
    const handleLoadMore = React.useCallback(
        (_startIndex: number, stopIndex: number) => {
            if (
                dialogTab === 0 &&
                !searchLoading &&
                !searchFetchMoreLoading &&
                !refetch &&
                hasNextPage &&
                searchData?.listingSearch?.matches?.length === currentTotal &&
                !!searchFetchMore
            ) {
                setSearchFetchMoreLoading(true)

                setCurrentTotal((prev) => prev + OPTIMISTIC_CARDS)

                return searchFetchMore({
                    variables: {
                        manufacturerProductSearchInput: {
                            ...getSearchInput(),
                            skip: searchData?.listingSearch?.matches?.length,
                            top: PER_PAGE,
                        },
                    },
                    updateQuery: (
                        prev: {
                            listingSearch: { count: any; matches: any }
                        },
                        { fetchMoreResult }: any
                    ) => {
                        setSearchFetchMoreLoading(false)
                        if (!fetchMoreResult) return prev
                        return {
                            listingSearch: {
                                ...prev.listingSearch,
                                count: fetchMoreResult.listingSearch.count,
                                matches: [
                                    ...prev.listingSearch.matches!,
                                    ...fetchMoreResult.listingSearch.matches!,
                                ],
                            },
                        }
                    },
                })
            } else if (
                dialogTab === 1 &&
                !searchLoading &&
                !searchFetchMoreLoading &&
                !refetch &&
                rHasNextPage &&
                relatedProducts?.listingSearch?.matches?.length ===
                currentRTotal &&
                !!getMoreRelatedProducts
            ) {
                setSearchFetchMoreLoading(true)
                setCurrentRTotal((prev) => prev + OPTIMISTIC_CARDS)

                return getMoreRelatedProducts({
                    variables: {
                        manufacturerProductSearchInput: {
                            facets: getRelatedProductsFacets([
                                ...(manufacturerProductSearchInput?.facets ??
                                    []),
                                {
                                    name: 'listingLevels',
                                    values: [ListingLevel.PremiumProprietary],
                                },
                                {
                                    name: 'categories',
                                    values: [category],
                                },
                            ] as Partial<FacetItem>[]),
                            productTypeSelections: [],
                            skip:
                                relatedProducts?.listingSearch?.matches?.length,
                            top: PER_PAGE,
                            logEventSource: LogEventSource.AeRelatedProducts,
                        },
                    },
                    updateQuery: (prev: any, { fetchMoreResult }: any) => {
                        setSearchFetchMoreLoading(false)
                        if (!fetchMoreResult) return prev
                        return {
                            listingSearch: {
                                ...prev.listingSearch,
                                count: fetchMoreResult.listingSearch.count,
                                matches: [
                                    ...prev.listingSearch.matches!,
                                    ...fetchMoreResult.listingSearch.matches!,
                                ],
                            },
                        }
                    },
                })
            }
            return null
        },
        [
            manufacturerProductSearchInput,
            searchLoading,
            searchFetchMoreLoading,
            refetch,
            searchData,
            currentTotal,
            currentRTotal,
            columnCount,
            hasNextPage,
            rHasNextPage,
            searchFetchMore,
            getMoreRelatedProducts,
            setSearchFetchMoreLoading,
            setCurrentTotal,
            getRelatedProductsFacets,
            dialogTab,
            category,
        ]
    )

    const isItemLoaded = (index: number) =>
        dialogTab === 0
            ? !!searchData?.listingSearch?.matches?.[index]
            : !!relatedProducts?.listingSearch?.matches?.[index]

    const setScrollRowAndColumn = React.useCallback(
        (rowIndex: number, columnIndex: number) => {
            setScrollState({ rowIndex, columnIndex })
        },
        []
    )

    const getSearchInput = React.useCallback(() => {
        const listingLevelFacet =
            manufacturerProductSearchInput.facets &&
            manufacturerProductSearchInput.facets.find(
                (facet) => facet.name === 'listingLevels'
            )?.values

        // Get family and product type filter count
        const familyFilterCount =
            manufacturerProductSearchInput.facets?.find(
                (facet) => facet.name === 'SectionNames'
            )?.values?.length || 0

        const ptFilterCount =
            manufacturerProductSearchInput.facets?.find(
                (facet) => facet.name === 'ProductType'
            )?.values?.length || 0

        // Set to assembly view if there are no family and product type filters
        const isChangeToAssemblyView =
            elementType !== 'ASSEMBLY' &&
            familyFilterCount + ptFilterCount === 0

        const isForDivisionViewFlag =
            isForDivisionView &&
            (elementType === 'ASSEMBLY' || isChangeToAssemblyView) &&
            !isAEAllProductsToggled

        let currentFacets = manufacturerProductSearchInput.facets || []

        if (!productsPage && !!manufacturerProductSearchInput.facets) {
            if (!!isAEAllProductsToggled) {
                // All products pill selected, remove assembly filter
                currentFacets = [
                    ...(currentFacets?.filter(
                        (facet) => facet.name !== 'assemblyNodeId'
                    ) || []),
                ]
            } else if (!isAssembly) {
                // If not in assembly view (division view)
                if (isChangeToAssemblyView && !!categoryNodeId) {
                    // No facet/filters for family and product type
                    // Replace assembly node with category node Id
                    currentFacets = [
                        ...(currentFacets?.filter(
                            (facet) => facet.name !== 'assemblyNodeId'
                        ) || []),
                    ]
                    currentFacets?.push({
                        name: 'assemblyNodeId',
                        values: [categoryNodeId],
                    })
                }
                // Remove SectionNames if empty as it causes issues
                if (familyFilterCount === 0) {
                    currentFacets = [
                        ...(currentFacets?.filter(
                            (facet) => facet.name !== 'SectionNames'
                        ) || []),
                    ]
                }
            }
        }

        if (specialty.includes('Manufacturer Options')) {
            currentFacets = [
                ...currentFacets.filter(
                    (past) => past.name !== 'withManufacturerOption'
                ),
                { name: 'withManufacturerOption', values: ['true'] },
            ]
        } else {
            currentFacets = currentFacets.filter(
                (facet) => facet.name !== 'withManufacturerOption'
            )
        }

        if (!productsPage && isOMUser) {
            currentFacets = [
                ...currentFacets,
                {
                    name: 'nodeIds',
                    values: [nodeId!],
                },
            ]
        }

        const searchInput: Partial<UpdatedListingSearchInput> =
            productsPage &&
                (!listingLevelFacet || listingLevelFacet.length === 0)
                ? {
                    ...manufacturerProductSearchInput,
                    facets: [
                        ...(manufacturerProductSearchInput.facets?.filter(
                            (facet) => facet.name !== 'listingLevels'
                        ) || []),
                        {
                            name: 'listingLevels',
                            values: defaultFilter,
                        },
                    ],
                }
                : {
                    ...manufacturerProductSearchInput,
                    isForDivisionView: isForDivisionViewFlag
                        ? true
                        : undefined,
                    facets: currentFacets,
                }
        return searchInput
    }, [
        manufacturerProductSearchInput,
        isForDivisionView,
        specialty,
        elementType,
        isAEAllProductsToggled,
        isAssembly,
        categoryNodeId,
        isOMUser,
        productsPage,
        nodeId,
    ])

    // Returns false if isForDivisionView is true and there are no valid facets
    const isDivisionFacetsValid = React.useCallback(
        (searchInput: Partial<UpdatedListingSearchInput>) => {
            let isValid = true
            if (!isAEAllProductsToggled && isForDivisionView) {
                // If there are no valid facets for division view, divisions are not valid
                if (!searchInput?.facets?.length) {
                    isValid = false
                } else {
                    const divisionFacetsCount =
                        (searchInput.facets?.find(
                            (facet) => facet.name === 'assemblyNodeId'
                        )?.values?.length || 0) +
                        (searchInput.facets?.find(
                            (facet) => facet.name === 'SectionNames'
                        )?.values?.length || 0) +
                        (searchInput.facets?.find(
                            (facet) => facet.name === 'ProductType'
                        )?.values?.length || 0)
                    isValid = divisionFacetsCount > 0
                }
            }
            return isValid
        },
        [isForDivisionView, isAEAllProductsToggled]
    )

    // Refetch
    const doRefetch = React.useCallback(() => {
        const searchInput = getSearchInput()
        // Fix for initial loading issue when in division view
        if (productsPage || isDivisionFacetsValid(searchInput)) {
            getListings({
                variables: {
                    manufacturerProductSearchInput: searchInput,
                },
            })

            const updatedSearchFacetInputs = manufacturerProductSearchInput.facets
                ? [...manufacturerProductSearchInput.facets]
                : []

            getRelatedProducts({
                variables: {
                    manufacturerProductSearchInput: {
                        ...manufacturerProductSearchInput,
                        facets: getRelatedProductsFacets(
                            updatedSearchFacetInputs
                        ),
                        productTypeSelections: [],
                        logEventSource: LogEventSource.AeRelatedProducts,
                    },
                },
            })

            setRefetch(false)
            setSearchLoading(true)
        }
    }, [
        getSearchInput,
        manufacturerProductSearchInput,
        getListings,
        getRelatedProducts,
        getRelatedProductsFacets,
        productsPage,
        isDivisionFacetsValid,
        dialogTab,
    ])

    const handleListingType = React.useCallback((value: string[]) => {
        setListingType(value)
        setSearchFacetInputs((past) => {
            return [
                ...past.filter((each) => each.name !== 'listingLevels'),
                {
                    name: 'listingLevels',
                    values: value,
                },
            ]
        })
    }, [])

    const handleProduct = React.useCallback(
        (value: Record<string, boolean>) => {
            setProduct(value)
        },
        []
    )

    const handleSpecialtyClick = useCallback(
        (value: string[]) => {
            setSpecialty(value)
            if (value.includes('Manufacturer Options')) {
                setManufacturerProductSearchInput((past) => ({
                    ...past,
                    facets: [
                        ...(past?.facets ?? []),
                        { name: 'withManufacturerOption', values: ['true'] },
                    ],
                }))
            } else {
                setManufacturerProductSearchInput((past) => ({
                    ...past,
                    facets: (past?.facets ?? []).filter(
                        (facet) => facet.name !== 'withManufacturerOption'
                    ),
                }))
            }

            setRefetch(true)
            setCurrentTotal(OPTIMISTIC_CARDS)
        },
        [specialty, OPTIMISTIC_CARDS]
    )

    const handlePartershipSelection = React.useCallback(
        (type: PartnershipType, value: string[]) => {
            if (type === 'Sustainability Resources') {
                setSustainabilityResources(value)
            } else {
                setBIMResources(value)
            }
        },
        [setSustainabilityResources, setBIMResources]
    )
    const handleUpdateWorkspaceManufacturerBP = (
        input: WorkspaceManufacturerListingItem
    ) => {
        const listingMatch = searchData?.listingSearch?.matches?.find(
            (each: Partial<ListingMatch>) => each.listingId === input?.listingId
        )
        // updating the local state to reflect changes in real time
        if (listingMatch) {
            const selectedListIndex = productSelected?.findIndex(
                (product: ProductListingSelected) =>
                    product?.listingId === listingMatch?.listingId
            )
            if (selectedListIndex) {
                productSelected[selectedListIndex].isBestPractice = input.isBP
                productSelected[selectedListIndex].isGlobalBestPractice =
                    input.isGBP
            }
            setProductSelected([...productSelected])
        }
        serviceWorker?.controller?.postMessage({
            [WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY]: {
                ...input,
            } as WorkspaceManufacturerListingItem,
        })
    }

    const updateWorkspaceItems = (
        contentItems: ContentItem[],
        input: Partial<CreateProjectManufacturerProductListingInput>
    ) => {
        serviceWorker?.controller?.postMessage({
            [WORKSPACE_MANUFACTURERS_CONTENT_ITEMS_KEY]: {
                listingId: input.listingId,
                contentItems: contentItems,
                isForBOD: input?.isBOD,
            } as WorkspaceManufacturerContentItems,
        })
        setRefetch(true)
    }

    const updateWorkspaceElementTree = (
        projectElementNodes: ProjectElementNode[],
        updatedElementId?: string
    ) => {
        serviceWorker?.controller?.postMessage({
            [WORKSPACE_TREE_ELEMENT_NODES]: {
                projectElementNodes: projectElementNodes,
                updatedElementId: updatedElementId,
            } as WorkspaceTreeElementNodes,
        })
        getSelectedProducts()
    }

    const addListingToProject = React.useCallback(
        (input: Partial<CreateProjectManufacturerProductListingInput>) => {
            setIsAddElementsLoading(true)

            const proceedWithListingElements = (
                nuggetProjectElementNodes?: ProjectElementNode[]
            ) => {
                addListingElements({ variables: { input, projectId } }).then(
                    (value) => {
                        if (
                            value?.data?.addManufacturerProductListingElements
                                .projectElementNodes === null &&
                            value?.data?.addManufacturerProductListingElements
                                .updatedInput === null
                        ) {
                            showGlobalSnackbar?.({
                                variables: {
                                    message:
                                        'Product Listing Product Type not found',
                                    variant: 'error',
                                    id: `ProductListingDialog-AddProductListingElementsNotFound`,
                                },
                            })
                        } else if (nuggetProjectElementNodes) {
                            // Merge nugget response into the addManufacturerProductListingElements response
                            if (
                                value?.data
                                    ?.addManufacturerProductListingElements
                            ) {
                                const existingNodes =
                                    value.data
                                        .addManufacturerProductListingElements
                                        .projectElementNodes || []
                                value.data.addManufacturerProductListingElements.projectElementNodes = [
                                    ...nuggetProjectElementNodes,
                                    ...existingNodes,
                                ]
                            }
                        }
                    }
                )
            }

            // Always proceed directly with listing elements
            // projectElementNodes will be handled in addListingElements response
            proceedWithListingElements()
        },
        [
            addListingElements,
            showGlobalSnackbar,
            projectNuggetData,
            elementId,
            projectId,
            updateWorkspaceElementTree,
            isBetaWorkspaceToggleEnabled,
            selectedProjectNode,
            elementType,
            isDivisionViewFromTreePath,
            isInProject,
        ]
    )

    const deleteListingMutation = React.useCallback(
        (input: Partial<DeleteProjectManufacturerProductListingInput>) => {
            setIsAddElementsLoading(true)
            deleteListing({ variables: { input } }).then((value) => {
                if (isProductType) {
                    serviceWorker?.controller?.postMessage({
                        [WORKSPACE_MANUFACTURERS_CONTENT_ITEMS_KEY]: {
                            listingId: input.listingId,
                            contentItems:
                                value.data
                                    ?.deleteProjectManufacturerProductListing,
                        } as WorkspaceManufacturerContentItems,
                    })
                }
                setRefetch(true)
                getSelectedProducts()
            })
        },
        [deleteListing, serviceWorker, getSelectedProducts, isProductType]
    )

    const getNodenEleIDs = (
        listingMatch: Partial<ListingMatch> | undefined,
        selectedPT: Partial<SearchFacets> | undefined
    ) => {
        const matchElement = productTypeFacets.find(
            (each) => each.parentProductFamily === listingMatch?.sectionName
        )
        return {
            listingMatchNodeId: selectedPT
                ? selectedPT.pTNodeId
                : matchElement
                    ? matchElement.pTNodeId
                        ? matchElement.pTNodeId
                        : matchElement.familyNodeId
                    : nodeId,
            listingMatchElementId: selectedPT
                ? selectedPT.pTElementId
                : matchElement
                    ? matchElement.pTElementId
                        ? matchElement.pTElementId
                        : matchElement.familyElementId
                    : elementId,
        }
    }

    const getProjectNuggetIdAndFamilyIdFromFacets = React.useCallback(
        (facets: Partial<SearchFacets>[] | undefined) => {
            return {
                projectNuggetId: facets?.find(
                    (node) => node.pTElementId !== null
                )?.pTElementId,
                familyElementId: facets?.find(
                    (node) => node.familyElementId !== null
                )?.familyElementId,
            }
        },
        []
    )

    const getProductTypeFacetFromListing = (
        facets: Partial<SearchFacets>[] | undefined,
        listing: Partial<ListingMatch> | undefined
    ) => {
        // Try to match both product type name and ID in order to correctly
        // identify PT copies (they have different name, same ID);
        // failing that, just match PT ID (PT name might not match listing
        // if PT was renamed)
        return (
            facets?.find(
                (item) =>
                    Number(item?.specAgentProductTypeId) ===
                    Number(listing?.specAgentProductTypeId) &&
                    item?.value == listing?.productType
            ) ??
            facets?.find(
                (item) =>
                    Number(item?.specAgentProductTypeId) ===
                    Number(listing?.specAgentProductTypeId)
            )
        )
    }

    const handleAddProductOption = React.useCallback(
        (
            listingId: string,
            selectionType: SelectionType,
            withManufacturerOption: boolean,
            withMasterSpecOption: boolean,
            displayTextFormat: DisplayTextFormatType
        ) => {
            const listingMatch = searchData?.listingSearch?.matches?.find(
                (each) => each.listingId === listingId
            )

            if (listingMatch) {
                updateSelectedListingId(listingId)

                const isSelectedItemFirstBOD =
                    productSelected?.filter(
                        (item) => item?.selectionType === 'BOD'
                    )?.length === 0 && selectionType === 'BOD'
                        ? true
                        : false

                const selectedPT = getProductTypeFacetFromListing(
                    productTypeFacets,
                    listingMatch
                )

                const {
                    listingMatchNodeId,
                    listingMatchElementId,
                } = getNodenEleIDs(listingMatch, selectedPT)

                let selectedNodeId =
                    isProductType || isProductFamily
                        ? nodeId
                        : listingMatchNodeId
                addListingToProject({
                    firmName: listingMatch.firmName,
                    isBOD: selectionType === 'BOD',
                    listingId: listingMatch.listingId,
                    productFamilyId: !!selectedPT
                        ? selectedPT?.familyElementId
                        : isProductFamily || isProductType
                            ? listingMatchElementId
                            : '',
                    productName: listingMatch.productName,
                    productType: !!selectedPT
                        ? selectedPT?.value
                        : listingMatch.productType ?? '',
                    projectId,
                    projectNuggetId: !!selectedPT
                        ? selectedPT?.pTElementId
                        : '',
                    elementId: listingMatchElementId,
                    projectElementType: !!selectedPT
                        ? ProjectElementType.PRODUCTTYPE
                        : isProductFamily || isProductType
                            ? ProjectElementType.PRODUCTFAMILY
                            : ProjectElementType.ASSEMBLY, // can be PRODUCTFAMILY, ASSEMBLIES, etc\
                    type: listingMatch.type?.split(' ').join('').toUpperCase(),
                    nodeId: selectedNodeId,
                    specAgentProductTypeId: listingMatch.specAgentProductTypeId?.toString(),
                    isAddMasterSpecAndManufacturerOptions: withManufacturerOption,
                    displayTextFormat:
                        displayTextFormat === 'Manufacturer'
                            ? ListingDisplayTextFormat.Manufacturer
                            : displayTextFormat === 'None'
                                ? ListingDisplayTextFormat.None
                                : ListingDisplayTextFormat.ManufacturerAndProduct,
                    ptpOptionsToInclude:
                        withMasterSpecOption && withManufacturerOption
                            ? PtpOptionsToInclude.MasterSpecAndManufacturerOptions
                            : withMasterSpecOption
                                ? PtpOptionsToInclude.MasterSpecOptions
                                : PtpOptionsToInclude.NoOptions,
                    isProductFamilyGroupView: isDivisionViewFromTreePath,
                } as Partial<CreateProjectManufacturerProductListingInput>)
                const selectedProductTemp = productSelected
                    .filter((each) => each.listingId !== listingMatch.listingId)
                    .map((item) => {
                        return {
                            ...item,
                            isBOD:
                                item.isBOD && selectionType === 'BOD'
                                    ? false
                                    : item.isBOD,
                            selectionType: 'Product' as SelectionType,
                            isActive:
                                item?.selectionType === 'BOD' &&
                                    selectionType === 'BOD'
                                    ? false
                                    : true,
                        }
                    })

                setProductSelected([
                    ...selectedProductTemp,
                    {
                        ...listingMatch,
                        selectionType: selectionType,
                        isBOD: selectionType === 'BOD',
                        isActive: selectionType === 'BOD',
                        hasSupportedManufacturersOptions: withManufacturerOption,
                        elementId: elementId as string,
                    },
                ])

                // Update the product count and send to service worker
                const newCount = (projectProductListingCount ?? 0) + 1
                setProjectProductListingCount(newCount)
                sendServiceWorkerMessage(newCount)

                if (isBetaWorkspaceToggleEnabled) {
                    dialogAdditionDataRef.current = {
                        listingId: listingMatch.listingId,
                        isSelected: true,
                        isFirstBOD: isSelectedItemFirstBOD,
                        listingText:
                            listingMatch.type ===
                                ListingLevel.PremiumMasterSpec ||
                                listingMatch.type === ListingLevel.Premium ||
                                listingMatch.type ===
                                ListingLevel.PremiumProprietary
                                ? `${listingMatch.firmName}:${listingMatch.productName}`
                                : `${listingMatch.firmName}`,
                        listingType: listingMatch.type
                            ?.split(' ')
                            .join('')
                            .toUpperCase(),
                        firmName: listingMatch.firmName,
                        isBOD: selectionType === 'BOD',
                        hasSupportedManufacturersOptions: withManufacturerOption,
                        elementId,
                        isBP: false,
                        isGBP: false,
                    }
                } else {
                    // OLD BEHAVIOR: Send service worker message immediately
                    serviceWorker?.controller?.postMessage({
                        [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                            listingId: listingMatch.listingId,
                            isSelected: true,
                            isFirstBOD: isSelectedItemFirstBOD,
                            listingText:
                                listingMatch.type ===
                                    ListingLevel.PremiumMasterSpec ||
                                    listingMatch.type === ListingLevel.Premium ||
                                    listingMatch.type ===
                                    ListingLevel.PremiumProprietary
                                    ? `${listingMatch.firmName}:${listingMatch.productName}`
                                    : `${listingMatch.firmName}`,
                            listingType: listingMatch.type
                                ?.split(' ')
                                .join('')
                                .toUpperCase(),
                            firmName: listingMatch.firmName,
                            isBOD: selectionType === 'BOD',
                        } as WorkspaceManufacturerSelected,
                    })
                }
            }
        },
        [
            serviceWorker,
            searchData,
            projectId,
            projectNuggetData,
            elementId,
            productSelected,
            setProductSelected,
            addListingToProject,
            isProductFamily,
            isProductType,
            productTypeFacets,
            searchFacetInputs,
            getNodenEleIDs,
            getProductTypeFacetFromListing,
            isBetaWorkspaceToggleEnabled,
            projectProductListingCount,
            setProjectProductListingCount,
            sendServiceWorkerMessage,
        ]
    )

    const handleRemoveProductOption = React.useCallback(
        (listingId: string, isBOD: boolean) => {
            const listingMatch = searchData?.listingSearch?.matches?.find(
                (each) => each.listingId === listingId
            )

            // Find the selected product to get the correct elementId that was stored when added
            const selectedProduct = productSelected.find(
                (each) => each.listingId === listingId
            )

            if (listingMatch) {
                updateSelectedListingId(listingId)
                const selectedPT = getProductTypeFacetFromListing(
                    productTypeFacets,
                    listingMatch
                )

                const {
                    listingMatchNodeId,
                    listingMatchElementId,
                } = getNodenEleIDs(
                    listingMatch ? listingMatch : ({} as Partial<ListingMatch>),
                    selectedPT
                )

                // If selectedPT not found, use the robust fallback function
                // This handles DivisionView scenarios where facet matching may fail
                const {
                    projectNuggetId: fallbackProjectNuggetId,
                    familyElementId: fallbackFamilyElementId,
                } = !selectedPT
                        ? getProjectNuggetIdAndFamilyIdFromFacets(productTypeFacets)
                        : {
                            projectNuggetId: selectedPT?.pTElementId,
                            familyElementId: selectedPT?.familyElementId,
                        }

                deleteListingMutation({
                    isBOD,
                    listingId,
                    productFamilyId:
                        fallbackFamilyElementId !== fallbackProjectNuggetId
                            ? fallbackFamilyElementId
                            : isProductFamily
                                ? listingMatchElementId
                                : undefined,
                    projectId,
                    projectNuggetId: selectedProduct?.elementId || listingMatchElementId || undefined,
                    isRetainProperties: false, // TODO: Change value if there is removal warning
                    firmName: listingMatch?.firmName,
                    type: listingMatch?.type?.replace(/\s/g, '').toUpperCase(),
                    nodeId: listingMatchNodeId,
                } as DeleteProjectManufacturerProductListingInput)

                setProductSelected((past) => [
                    ...past.filter((each) => each.listingId !== listingId),
                ])
                serviceWorker?.controller?.postMessage({
                    [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                        listingId: listingId,
                        isSelected: false,
                        hasSupportedManufacturersOptions: false,
                        isBOD,
                    } as WorkspaceManufacturerSelected,
                })
                const selectedListingCount = projectProductListingCount
                    ? projectProductListingCount - 1
                    : 0
                setProjectProductListingCount(selectedListingCount)
                sendServiceWorkerMessage(selectedListingCount)
                setManufacturerProductSearchInput((past) => ({
                    ...past,
                    projectId,

                    listingIdsList: past.listingIdsList?.filter(
                        (each) => each !== listingId
                    ),
                }))
            }
        },
        [
            isProductFamily,
            projectNuggetData,
            serviceWorker,
            deleteListingMutation,
            projectId,
            elementId,
            searchData,
            projectProductListingCount,
            setProjectProductListingCount,
            sendServiceWorkerMessage,
            isProductType,
            nodeId,
            autosuggestCardData,
            productTypeFacets,
            getNodenEleIDs,
            getProductTypeFacetFromListing,
            getProjectNuggetIdAndFamilyIdFromFacets,
            productSelected,
        ]
    )

    const handleModifyProductOption = (
        listingId: string,
        selectionType: SelectionType
    ) => {
        setProductSelected((past) => [
            ...past.map((each) => {
                if (each.listingId === listingId) {
                    return {
                        ...each,
                        selectionType,
                    }
                }
                return each
            }),
        ])
    }

    const unloadListener = React.useCallback(() => {
        updatePersistentConfigs(
            userId,
            projectId,
            'projectProductDialogOpened',
            false
        )
    }, [projectId, userId])

    const updateSelectedListingId = React.useCallback(
        (listingId) => {
            updatePersistentConfigs(
                userId,
                projectId,
                'selectedListingId',
                listingId
            )
        },
        [projectId, userId]
    )
    const getSelectedListingId = React.useCallback(() => {
        return getFromLocalStorage(userId!, projectId!, 'selectedListingId')
    }, [projectId, userId])

    const getFacetSelectedOptions = (facet: ProductListingDialogFacet) => {
        const filtered = searchFacetInputs.find((each) => each.name === facet)
        if (facet === 'ProductType') {
            const productTypeValues = getProductTypeSelectionsData(
                productTypeFacets,
                searchFacetInputs
            )
            return productTypeValues?.map((each) => {
                return `${each?.value} || ${each?.parentProductFamily}`
            })
        }
        return filtered?.values.sort((a, b) => a.localeCompare(b)) || []
    }

    const getFacetOptions = (facet: ProductListingDialogFacet) => {
        const filtered = searchFacets.find((each) => each.name === facet)

        let addedToProjectItems = searchData?.listingSearch.matches?.filter(
            (matchedListing) =>
                productSelectedListingIds.some(
                    (id) => id === matchedListing.listingId
                )
        )

        return product[productOptions.ADDED_TO_PROJECT] && facet === 'FirmName'
            ? filtered?.values
                ?.filter((filteredItem) =>
                    addedToProjectItems?.some(
                        (addedToProdectListing) =>
                            addedToProdectListing.firmName ===
                            filteredItem.value
                    )
                )
                ?.map((each) => each.value!)
                .sort((a, b) => a.localeCompare(b)) || []
            : filtered?.values
                ?.map((each) => each.value!)
                .sort((a, b) => a.localeCompare(b)) || []
    }

    const getProductFacetOptions = React.useCallback(
        (facet: ProductListingDialogFacet) => {
            return (
                autosuggestCardData?.listingCardsAutoSuggest?.productFamilyToProductTypes
                    ?.map((each) => ({
                        value: each?.productType ?? '',
                        parentProductFamily: each?.parentProductFamily ?? '',
                        specAgentProductTypeId:
                            each?.specAgentProductTypeId ?? '',
                        pTElementId: each.pTElementId,
                        pTNodeId: each.pTNodeId,
                        familyElementId: each.familyElementId,
                        familyNodeId: each.familyNodeId,
                    }))
                    .sort((a, b) => a.value.localeCompare(b.value)) || []
            )
        },
        [autosuggestCardData]
    )

    const handleFacetSelectedChange = React.useCallback(
        (
            facet: ProductListingDialogFacet,
            value: string[],
            productTypeFacets?: Partial<SearchFacets>[],
            inputValue?: string
        ) => {
            if (
                productTypeFacets &&
                productTypeFacets?.length !== 0 &&
                !autosuggestCardLoading
            ) {
                setProductTypeFacets((past) => {
                    return removeDuplicateProductTypeFacets([
                        ...productTypeFacets,
                        ...past,
                    ])
                })
            }
            setSearchFacetInputs((past) => {
                return [
                    ...past.filter(
                        (each) =>
                            each.name?.toLowerCase() !== facet?.toLowerCase()
                    ),
                    {
                        name: facet,
                        values: value,
                    },
                ]
            })
            setManufacturerProductSearchInput((past) => ({
                ...past,
                projectId,

                listingIdsList: past.listingIdsList?.filter(
                    (each) => each !== listingId
                ),
            }))
        },
        [autosuggestCardLoading, listingId, setProductTypeFacets]
    )

    const setHeaderPillToggle = React.useCallback(
        (isToggled: boolean) => {
            if (isToggled !== isAEAllProductsToggled) {
                getSelectedProducts()
                getProductFlaggedForReviewProducts()
                setIsAEAllProductsToggled(isToggled)
                setSpecialty([])
                if (!!isToggled) {
                    // All Products / Clear filters
                    handleFacetSelectedChange?.('SectionNames', [] as string[])
                    handleFacetSelectedChange?.('ProductType', [] as string[])
                } else {
                    // Assembly / Restore PF and PT filter selections if any
                    if (hasFamily && !!family) {
                        handleFacetSelectedChange?.('SectionNames', [
                            family as string,
                        ])
                    } else {
                        handleFacetSelectedChange?.(
                            'SectionNames',
                            [] as string[]
                        )
                    }
                    if (hasProductType && !!productType) {
                        handleFacetSelectedChange?.('ProductType', [
                            productType as string,
                        ])
                    } else {
                        handleFacetSelectedChange?.(
                            'ProductType',
                            [] as string[]
                        )
                    }
                }
                handleFacetSelectedChange?.('FirmName', [] as string[])
                // Clear advance filters
                handleProduct(defaultProduct) // 'All Listings'
                handlePartershipSelection?.(
                    'Sustainability Resources',
                    [] as string[]
                )
                handlePartershipSelection?.('BIM Resources', [] as string[])
                handleListingType([] as string[])
            }
        },
        [
            isAEAllProductsToggled,
            getSelectedProducts,
            getProductFlaggedForReviewProducts,
            setIsAEAllProductsToggled,
            handleFacetSelectedChange,
            handleProduct,
            handlePartershipSelection,
            handleListingType,
            hasFamily,
            family,
            hasProductType,
            productType,
        ]
    )

    const apolloClient = useApolloClient()

    // Function to get all family nodeIds under current assembly
    const getAllFamilyNodeIdsUnderAssembly = React.useCallback(async () => {
        if (!nodeId || !projectId) return null

        try {
            const result = await apolloClient.query({
                query: GetProjectElementNodesQuery,
                variables: {
                    input: {
                        projectId: projectId,
                        parentNodeId: nodeId, // Current assembly
                        projectElementType: [
                            ProjectElementType.ASSEMBLY,
                            ProjectElementType.PRODUCTFAMILY,
                        ],
                        onlyElementsAssignedToProject: true,
                        isFromAEWorkSpace: true,
                        skip: 0,
                        limit: 1000,
                        categoryId: undefined,
                        isProductFamilyGroupView: false,
                        searchFilters: undefined,
                    },
                },
                fetchPolicy: 'no-cache',
            })

            const data = result.data as GetProjectElementNodesResponse
            const familyNodes = data?.getProjectElementNodes?.projectElementNodes?.filter(
                (node: any) =>
                    node.elementType === ProjectElementType.PRODUCTFAMILY
            )

            return familyNodes?.map((node: any) => node.id) || null
        } catch (error) {
            console.error('Error fetching family nodes:', error)
            return null
        }
    }, [nodeId, projectId, apolloClient])

    const getSelectedFamilyNodeIds = React.useCallback(
        (facet: ProductListingDialogFacet) => {
            if (facet === 'ProductType') {
                const familyNames = searchFacetInputs.find(
                    (item) => item.name === 'SectionNames'
                )

                const selectedFamilies = productTypeFacets
                    .filter((fam) =>
                        familyNames?.values.some(
                            (each) =>
                                !!fam.familyNodeId &&
                                (isProductFamily
                                    ? true
                                    : nodeId !== fam.familyNodeId) &&
                                each.trim() === fam.parentProductFamily?.trim()
                        )
                    )
                    ?.reduce((prev: Partial<SearchFacets>[], current) => {
                        const isExist = prev.find(
                            (item) =>
                                item.parentProductFamily ===
                                current.parentProductFamily
                        )
                        if (!isExist) {
                            return prev.concat([current])
                        } else {
                            return prev
                        }
                    }, [])
                const familyNodeIds = selectedFamilies.flatMap(
                    (item) => item.familyNodeId
                )

                const uniquesFamilyNodeIds = Array.from(new Set(familyNodeIds))

                if (uniquesFamilyNodeIds.length > 0) {
                    return uniquesFamilyNodeIds
                } else if (isProductType && !!parentNodeId) {
                    // if AllProducts is toggled and no Family is selected, we are returning null, this will fetch 50 initial suggestsion
                    if (
                        isAEAllProductsToggled ||
                        (dialogTab === 1 && uniquesFamilyNodeIds.length === 0)
                    ) {
                        return null
                    } else {
                        //if no family node is selected and PT is selected, we are going to return the category node,
                        // Use category if no family is selected
                        return uniquesFamilyNodeIds.length === 0 &&
                            !!categoryNodeId
                            ? [categoryNodeId]
                            : [parentNodeId]
                    }
                } else if (nodeId && !isAEAllProductsToggled) {
                    //if we are using related products, we are going to treat this as all products
                    if (familyNodeIds.length === 0) {
                        //no selected family, we're going to get all the product types under the assembly
                        return null
                    }
                    return dialogTab === 1 ? null : [nodeId]
                } else {
                    return null
                }
            }

            if (isProductType && !!parentNodeId) {
                // if AllProducts is toggled and no Family is selected, we are returning null, this will fetch 50 initial data
                return isAEAllProductsToggled || dialogTab === 1
                    ? null
                    : [parentNodeId]
            } else if (nodeId) {
                // if AllProducts is toggled and no Family is selected, we are returning null, this will fetch 50 initial suggestsion
                return isAEAllProductsToggled || dialogTab === 1
                    ? null
                    : [nodeId]
            } else {
                return null
            }
        },
        [
            nodeId,
            productTypeFacets,
            searchFacetInputs,
            parentNodeId,
            isProductType,
            isProductFamily,
            dialogTab,
        ]
    )

    const fetchAutoSuggest = React.useCallback(
        async (facet: ProductListingDialogFacet, value: string) => {
            setAutosuggestFacet({ name: facet, value })
            setSearchFacets((past) => [
                ...past.filter(
                    (each) => each.name?.toLowerCase() !== facet.toLowerCase()
                ),
            ])

            if (facet === 'ProductType' || facet === 'SectionNames') {
                // Get all family nodeIds under current assembly instead of selected ones
                const allFamilyNodeIds =
                    elementType === 'ASSEMBLY'
                        ? await getAllFamilyNodeIdsUnderAssembly()
                        : getSelectedFamilyNodeIds(facet)

                getListingCardAutosuggest({
                    variables: {
                        input: {
                            autoSuggestField:
                                facet === 'ProductType'
                                    ? ('PRODUCTTYPE' as AutoSuggestFieldType)
                                    : ('PRODUCTFAMILY' as AutoSuggestFieldType),
                            nodeIds: allFamilyNodeIds,
                            searchText: value,
                            limit: 50,
                            skip: 0,
                            projectId: !isAEAllProductsToggled
                                ? projectId!
                                : undefined,
                            partnerships: sustainabiliyResources,
                            firmName: searchFacetInputs?.find(
                                (facet) => facet?.name === 'FirmName'
                            )?.values,
                            isPremiumProprietary: dialogTab === 1,
                            divisionNumber: isAEAllProductsToggled
                                ? ''
                                : divisionNumber,
                            //when viewing related products, we're treating the autosuggest as for all products.
                            isForAllProducts:
                                dialogTab === 1 ? true : isAEAllProductsToggled,
                        },
                    },
                })
            } else {
                let tempFacets = [
                    ...searchFacetInputs.filter(
                        (each) =>
                            each.name?.toLowerCase() !== facet.toLowerCase()
                    ),
                ]

                // We need to set the listing level to premium proprietary to the facets
                // if the product listing dialog is on the related products tab
                if (dialogTab === 1) {
                    tempFacets = tempFacets.filter((facet) => {
                        return facet.name !== 'listingLevels'
                    })
                    tempFacets.push({
                        name: 'listingLevels',
                        values: [ListingLevel.PremiumProprietary],
                    })
                }

                // Need to match case of API for this to work correctly.
                tempFacets = tempFacets.map((each) => {
                    let facet = { ...each }
                    if (each.name === 'ProductType') {
                        facet = { ...facet, name: 'productType' }
                    }
                    if (each.name === 'SectionNames') {
                        facet = { ...facet, name: 'sectionNames' }
                    }
                    return facet
                })

                getListingsAutosuggest({
                    variables: {
                        input: {
                            facets: tempFacets,
                            searchLimit: 50,
                            searchField: facet,
                            searchText: value,
                            productTypeSelections:
                                dialogTab === 1
                                    ? null
                                    : !isAEAllProductsToggled
                                        ? productTypeSelections
                                        : undefined,
                            projectId: !isAEAllProductsToggled
                                ? projectId
                                : undefined,
                        },
                    },
                })
            }
        },
        [
            productTypeSelections,
            searchFacetInputs,
            nodeId,
            projectId,
            sustainabiliyResources,
            getListingCardAutosuggest,
            getListingsAutosuggest,
            dialogTab,
            divisionNumber,
            queryParams,
            isAEAllProductsToggled,
            getAllFamilyNodeIdsUnderAssembly,
            getSelectedFamilyNodeIds,
            elementType,
        ]
    )

    const handleAutocompleteInputValueChange = React.useCallback(
        debounce(fetchAutoSuggest),
        [fetchAutoSuggest]
    )

    const handleAutocompleteNameChange = React.useCallback(
        (facet?: ProductListingDialogFacet) => {
            if (!!facet && !isProductTypeFacetsLoading) {
                fetchAutoSuggest(facet, '')
            }
        },
        [fetchAutoSuggest, isProductTypeFacetsLoading]
    )

    const debounceUpdate = React.useCallback(
        debounce(
            (workspaceEnabledPTPOptions: ProductTypePropertiesInputType[]) => {
                setProductTypeSelections(
                    defaultProductTypeSelections(
                        hasFamily,
                        family as string,
                        hasProductType,
                        productType as string,
                        workspaceEnabledPTPOptions
                    )
                )
                setOrderBy(defaultListingOrder)
                setPropertyOptions(workspaceEnabledPTPOptions)
            },
            500
        ),
        [
            hasFamily,
            family,
            hasProductType,
            productType,
            isAssembly,
            isLastSubAssembly,
        ]
    )
    const onRemoveProductListing = (listingId: string, isBOD: boolean) => {
        setListingInfoToDelete({ listingId, isBOD: isBOD ?? false })
        setIsRemoveWarningOpen(!isRemoveWarningOpen)
    }

    const triggerRemoveProductListing = () => {
        if (listingInfoToDelete) {
            handleRemoveProductOption(
                listingInfoToDelete.listingId,
                listingInfoToDelete.isBOD
            )
            setListingInfoToDelete(undefined)
            setIsRemoveWarningOpen(!isRemoveWarningOpen)
        }
    }

    const closeWarningDialog = () => {
        setListingInfoToDelete(undefined)
        setIsRemoveWarningOpen(!isRemoveWarningOpen)
    }

    const resetProductSearchQueryInput = (
        workspaceManufacturerSelectedState: WorkspaceManufacturerSelected
    ) => {
        setManufacturerProductSearchInput((past) => ({
            ...past,
            projectId,

            listingIdsList: workspaceManufacturerSelectedState.isSelected
                ? [
                    ...((past?.listingIdsList ?? []) as string[]),
                    workspaceManufacturerSelectedState.listingId,
                ]
                : past.listingIdsList?.filter(
                    (each) =>
                        each !== workspaceManufacturerSelectedState.listingId
                ),
            productTypeSelections: past.productTypeSelections?.filter(function (
                props
            ) {
                props.productTypeProperties = []
                return true
            }),
            orderBy: defaultListingOrder,
        }))
    }
    React.useEffect(() => {
        if (calculateEmptyListings()) {
            // do not query if there is no selected listing id and Added To Project Only is selected
            // and do not query if there is no flagged for review and Flagged for review is selected
            return
        }

        let includedListingIds: string[] = []
        const listingIdMap: Record<string, string[]> = {
            [productOptions.ADDED_TO_PROJECT]: productSelectedListingIds,
            [productOptions.FLAGGED_FOR_REVIEW]: flaggedForReviewListingIds,
        }

        Object.entries(product).map(([key, included]) => {
            if (!!included) {
                includedListingIds = [
                    ...includedListingIds,
                    ...listingIdMap[key],
                ]
            }
        })

        const productTypeSelections =
            manufacturerProductSearchInput.productTypeSelections

        setManufacturerProductSearchInput((past) => ({
            ...past,
            projectId,
            listingIdsList: includedListingIds,
            productTypeSelections: product[productOptions.ADDED_TO_PROJECT]
                ? productTypeSelections?.map((productTypeSelection) => {
                    return {
                        ...productTypeSelection,
                        productTypeProperties: [],
                    }
                })
                : productTypeSelections,
        }))
        setRefetch(true)
        setCurrentTotal(OPTIMISTIC_CARDS)
    }, [
        product,
        projectId,
        productSelectedListingIds,
        flaggedForReviewListingIds,
        OPTIMISTIC_CARDS,
        calculateEmptyListings,
    ])

    React.useEffect(() => setHeaderVisibility(false), [setHeaderVisibility])

    React.useEffect(
        () => setProductsPage(location.pathname.includes('products')),
        [location]
    )
    const getMultiplePTsWithBOD = React.useCallback(() => {
        if (dialogTab === 0) {
            const selectedFamilyFilters = searchFacetInputs?.find(
                (item) => item.name === 'SectionNames'
            )?.values
            if (selectedFamilyFilters && selectedFamilyFilters.length !== 0) {
                const familyNodeIds = productTypeFacets
                    ?.filter((item) => {
                        return (
                            selectedFamilyFilters?.some(
                                (itemFamily) =>
                                    itemFamily === item?.parentProductFamily
                            ) && item?.familyNodeId
                        )
                    })
                    .map((item) => {
                        return item.familyNodeId || ''
                    })
                    .filter((item) => {
                        return item !== ''
                    })
                onGetProductTypesWithBod(
                    !isDivisionSelected && !!familyNodeIds?.length
                        ? Array.from(new Set(familyNodeIds))
                        : undefined
                )
            } else {
                onGetProductTypesWithBod(
                    isDivisionSelected || !nodeId ? undefined : [nodeId]
                )
            }
        }
    }, [
        productTypeFacets,
        searchFacetInputs,
        dialogTab,
        isAssembly,
        nodeId,
        getProductTypesWithBod,
        isForDivisionView,
        projectId,
        productTypeWithBODForDivisionInput,
        isDivisionSelected,
    ])

    React.useEffect(() => {
        const partnershipFilters = [...bimResources, ...sustainabiliyResources]

        setSearchFacetInputs((past) => {
            return [
                ...past.filter((each) => each.name !== 'partnerships'),
                {
                    name: 'partnerships',
                    values: partnershipFilters,
                },
            ]
        })
    }, [bimResources, sustainabiliyResources])

    // clear all of the advanced filters not the dropdowns
    const handleClearAllClick = useCallback(() => {
        setListingType([])
        setProduct(defaultProduct)
        setBIMResources([])
        setSustainabilityResources([])
        setSpecialty([])
        setSearchFacets([])
        setRefetch(true)

        setManufacturerProductSearchInput(
            ({ listingIdsList, facets, ...past }) => ({
                facets: facets?.filter(
                    (facet) => facet.name !== 'withManufacturerOption'
                ),
                ...past,
            })
        )

        setSearchFacetInputs((past) => [
            ...past.filter((each) => each.name !== 'listingLevels'),
            {
                name: 'listingLevels',
                values: [],
            },
        ])
    }, [defaultProduct, defaultManufacturerProductSearchInput])

    const removeProductType = React.useCallback(
        (value: string[]) => {
            let familyProductTypeList: string[] = []
            if (value && value.length > 0) {
                value.forEach((val) => {
                    const tempList = productTypeFacets
                        .filter((each) => each.parentProductFamily === val)
                        ?.flatMap((ele) => ele.value ?? '')
                    const filteredVal = tempList?.filter(
                        (each) => each && each !== ''
                    )
                    if (filteredVal && filteredVal.length > 0) {
                        familyProductTypeList = [
                            ...familyProductTypeList,
                            ...filteredVal,
                        ]
                    }
                })
            }
            const existingProductTypeList = searchFacetInputs.find(
                (each) => each.name === 'ProductType'
            )
            const productTypeList = existingProductTypeList?.values.filter(
                (n) => {
                    return familyProductTypeList.indexOf(n) !== -1
                }
            )
            handleFacetSelectedChange('SectionNames', value)
            handleFacetSelectedChange('ProductType', productTypeList ?? [])
        },
        [productTypeFacets, handleFacetSelectedChange, searchFacetInputs]
    )

    const getBreadcrumbsWithCategory = React.useCallback((): string => {
        return category && assemblyPath
            ? `${category} / ${assemblyPath}`
            : category ?? ''
    }, [category, assemblyPath])

    const updateIsBannerExpanded = React.useCallback(
        (value: boolean) => {
            setIsBannerExpanded(value)
        },
        [setIsBannerExpanded]
    )

    React.useEffect(() => {
        if (projectId && selectedProjectNode) {
            getSelectedProducts()
        }
    }, [projectId, getSelectedProducts, selectedProjectNode])

    React.useEffect(() => {
        if (productsPage) {
            setSearchFacetInputs((past) => [
                ...past.filter((each) => each.name !== 'submissionStatus'),
            ])
            setOrderBy('lastUpdated desc')
        }
    }, [productsPage])

    React.useEffect(() => {
        if (!autosuggestLoading && autosuggestData && !autosuggestSaved) {
            setSearchFacets((past) => [
                ...past.filter((each) => each.name !== autosuggestFacet.name),
                {
                    name: autosuggestFacet.name,
                    values:
                        autosuggestData.listingAutoSuggest.result?.map(
                            (each) => ({
                                value: each,
                            })
                        ) ?? [],
                },
            ])
            setAutosuggestSaved(true)
        }
    }, [
        autosuggestData,
        autosuggestLoading,
        autosuggestFacet.name,
        autosuggestSaved,
    ])

    React.useEffect(() => {
        if (
            !autosuggestCardLoading &&
            autosuggestCardData &&
            !autosuggestCardSaved
        ) {
            if (autosuggestFacet?.name === 'ProductType') {
                setSearchFacets((past) => [
                    ...past.filter(
                        (each) =>
                            each.name?.toLowerCase() !==
                            autosuggestFacet?.name?.toLowerCase()
                    ),
                    {
                        name: 'ProductType',
                        values: autosuggestCardData?.listingCardsAutoSuggest?.productFamilyToProductTypes?.map(
                            (each) => ({
                                value: each?.productType ?? '',
                                parentProductFamily:
                                    each?.parentProductFamily ?? '',
                                specAgentProductTypeId:
                                    each?.specAgentProductTypeId ?? '',
                            })
                        ),
                    },
                ])
                if (isProductTypeFacetsLoading) {
                    const updatedPTFacets =
                        autosuggestCardData?.listingCardsAutoSuggest
                            ?.productFamilyToProductTypes
                    const ptNames = searchFacetInputs.find(
                        (i) => i.name === 'ProductType'
                    )
                    const filteredUpdatedPTFacets =
                        updatedPTFacets?.filter((item) =>
                            ptNames?.values.some(
                                (each) => each === item.productType
                            )
                        ) ?? []
                    const newSelectedPTFacets = filteredUpdatedPTFacets.map(
                        (item) => {
                            return {
                                parentProductFamily: item.parentProductFamily,
                                pTNodeId: item.pTNodeId,
                                pTElementId: item.pTElementId,
                                familyElementId: item.familyElementId,
                                familyNodeId: item.familyNodeId,
                                specAgentProductTypeId:
                                    item.specAgentProductTypeId,
                                value: item.productType,
                            } as Partial<SearchFacets>
                        }
                    )
                    setProductTypeFacets((past) =>
                        removeDuplicateProductTypeFacets([
                            ...newSelectedPTFacets,
                            ...past,
                        ] as Partial<SearchFacets>[])
                    )
                    setIsProductTypeFacetsLoading(false)
                }
            }
            if (autosuggestFacet?.name === 'SectionNames') {
                setSearchFacets((past) => [
                    ...past.filter(
                        (each) =>
                            each.name?.toLowerCase() !==
                            autosuggestFacet?.name?.toLowerCase()
                    ),
                    {
                        name: 'SectionNames',
                        values: autosuggestCardData?.listingCardsAutoSuggest?.productFamilyToProductTypes?.map(
                            (each) => {
                                return {
                                    value: each?.parentProductFamily ?? '',
                                }
                            }
                        ),
                    },
                ])
                if (isProductTypeFacetsLoading) {
                    const updatedPTFacets =
                        autosuggestCardData?.listingCardsAutoSuggest
                            ?.productFamilyToProductTypes
                    const familyNames = searchFacetInputs.find(
                        (i) => i.name === 'SectionNames'
                    )
                    const filteredUpdatedPTFacets =
                        updatedPTFacets?.filter((item) =>
                            familyNames?.values.some(
                                (each) => each === item.parentProductFamily
                            )
                        ) ?? []
                    const newSelectedPTFacets = filteredUpdatedPTFacets.map(
                        (item) => {
                            return {
                                parentProductFamily: item.parentProductFamily,
                                pTNodeId: item.pTNodeId,
                                pTElementId: item.pTElementId,
                                familyElementId: item.familyElementId,
                                familyNodeId: item.familyNodeId,
                                specAgentProductTypeId:
                                    item.specAgentProductTypeId,
                                value: item.productType,
                            } as Partial<SearchFacets>
                        }
                    )
                    setProductTypeFacets((past) =>
                        removeDuplicateProductTypeFacets([
                            ...newSelectedPTFacets,
                            ...past,
                        ])
                    )
                    fetchAutoSuggest('ProductType', '')
                }
            }
            setAutosuggestCardSaved(true)
        }
    }, [
        autosuggestCardData,
        autosuggestCardLoading,
        autosuggestFacet.name,
        autosuggestCardSaved,
        isProductTypeFacetsLoading,
        searchFacetInputs,
    ])

    // Used to reset the scrollbar
    React.useEffect(() => {
        if (previousDialogTab === dialogTab) return
        if (hasMountedRef.current) {
            if (infiniteLoaderRef) {
                const list = infiniteLoaderRef.current as any
                if (list) {
                    const grid = list._listRef as FixedSizeGrid
                    list.resetloadMoreItemsCache() //Reset infinite scroll
                    grid.scrollToItem({
                        align: 'start',
                        columnIndex: 0,
                        rowIndex: 0,
                    }) //scroll to top after refresh
                    list.scrollTop = 0 //firefox solution for scroll not reseting
                }
            }
        }
        hasMountedRef.current = true
    }, [
        refetch,
        searchLoading,
        dialogTab,
        searchFetchMoreLoading,
        getListingsLoading,
    ])

    // Used to trigger rescroll after adding/removing listing
    React.useEffect(() => {
        if (!searchLoading && !refetch && !searchFetchMoreLoading) {
            const selectedListingId = getSelectedListingId()
            const count = searchData?.listingSearch?.matches?.length
            if (selectedListingId && count) {
                const index =
                    searchData?.listingSearch?.matches?.findIndex(
                        (each) => each.listingId === selectedListingId
                    ) ?? -1
                if (index > -1 && isItemLoaded(index)) {
                    const row = Math.ceil((index + 1) / columnCount) - 1
                    const col = index % columnCount
                    setScrollRowAndColumn(row, col)
                    setReScroll(true)
                    updateSelectedListingId('')
                } else {
                    // scroll to bottom to fetch more
                    const row = Math.ceil((count + 1) / columnCount) - 1
                    setScrollRowAndColumn(row, 0)
                    setReScroll(true)
                }
            }
        }
    }, [
        searchData?.listingSearch?.matches,
        searchLoading,
        refetch,
        searchFetchMoreLoading,
    ])

    // Used to rescroll to the selected product
    React.useEffect(() => {
        if (reScroll)
            if (infiniteLoaderRef) {
                const list = infiniteLoaderRef.current as any
                if (list) {
                    list._listRef.scrollToItem({
                        align: 'start',
                        rowIndex: scrollState.rowIndex,
                        columnIndex: scrollState.columnIndex,
                    })
                    setReScroll(false)
                }
            }
    }, [scrollState, reScroll])

    const updateProductTypeFacets = React.useCallback(
        (facet: ProductListingDialogFacet) => {
            setIsProductTypeFacetsLoading(true)
            fetchAutoSuggest(facet, '')
        },
        [searchFacetInputs, fetchAutoSuggest]
    )

    // Handles dialog update when a different node was clicked
    React.useMemo(() => {
        if (productsPage) return

        if (
            !!isAEAllProductsToggled &&
            !!previousNodeId &&
            previousNodeId !== nodeId
        ) {
            // Return to non all products when selecting different node
            setHeaderPillToggle(false)
        }

        if (
            (hasProductType &&
                previousProductType === productType &&
                previousFamily === family &&
                previousAssemblyPath === assemblyPath) ||
            (!hasProductType &&
                hasFamily &&
                previousFamily === family &&
                previousAssemblyPath === assemblyPath &&
                previousProductType === null) ||
            (!hasProductType &&
                !hasFamily &&
                !isAssembly &&
                isLastSubAssembly &&
                previousAssemblyPath === assemblyPath &&
                previousFamily === null &&
                previousProductType === null)
        ) {
            updateProductTypeFacets('SectionNames')
            return
        }

        setCloseSlide(true)
        setProductTypeFacets(defaultProductTypeFacets)
        setSearchFacetInputs((past) => {
            let filtered = [
                ...past.filter(
                    (each) =>
                        each.name !== 'ProductType' &&
                        each.name !== 'SectionNames' &&
                        each.name !== 'categories' &&
                        each.name !== 'assemblyNodeId'
                ),
            ]
            if (hasProductType) {
                filtered = [
                    ...filtered,
                    {
                        name: 'ProductType',
                        values: [productType as string],
                    },
                ]
            }

            if (hasFamily) {
                filtered = [
                    ...filtered,
                    {
                        name: 'SectionNames',
                        values: [family as string],
                    },
                ]
            }

            if (category && category !== 'All Categories') {
                filtered = [
                    ...filtered,
                    {
                        name: 'categories',
                        values: [category],
                    },
                ]
            }
            if (
                !isProductFamily &&
                !isProductType &&
                (isAssembly || isLastSubAssembly)
            ) {
                filtered = [
                    ...filtered,
                    { name: 'assemblyNodeId', values: [nodeId as string] },
                ]
            }
            return [...filtered]
        })
    }, [
        family,
        hasFamily,
        category,
        productsPage,
        hasProductType,
        productType,
        isProductFamily,
        isProductType,
        isAssembly,
        isLastSubAssembly,
        nodeId,
        previousFamily,
        previousAssemblyPath,
        previousProductType,
        isAEAllProductsToggled,
        elementId,
        previousNodeId,
    ])

    const selectedProductTypesWithManufacturerOption = React.useMemo(() => {
        return productSelected
            ?.filter(
                (selectedProduct: ProductListingSelected) =>
                    !!selectedProduct?.hasSupportedManufacturersOptions
            )
            ?.map((each) => each?.productType?.toUpperCase())
    }, [productSelected])

    React.useMemo(() => {
        if (productsPage) return

        let localStoragePtpOptions:
            | ProductTypePropertiesInputType[]
            | undefined = undefined

        if (!Object.values(product).some((value) => !!value))
            localStoragePtpOptions = getFromLocalStorage(
                userId!,
                projectId!,
                'propertyOption'
            ) as ProductTypePropertiesInputType[]

        setProductTypeSelections(
            defaultProductTypeSelections(
                hasFamily,
                family as string,
                hasProductType,
                productType as string,
                localStoragePtpOptions ?? undefined
            )
        )

        setOrderBy(defaultListingOrder)
    }, [
        userId,
        projectId,
        family,
        hasFamily,
        productsPage,
        hasProductType,
        productType,
        product,
        isAssembly,
        isLastSubAssembly,
    ])
    // TODO: Refector code to change the input
    // For now, update to this hook should be done one at a time
    React.useMemo(() => {
        if (!orderBy) return

        let isAddedToProjectOnly = product[productOptions.ADDED_TO_PROJECT]
        const ptps = isAddedToProjectOnly
            ? productTypeSelections?.map((productTypeSelection) => {
                return {
                    ...productTypeSelection,
                    productTypeProperties: [],
                }
            })
            : productTypeSelections

        //get selected inputs in product type filter
        const selectedProductTypeInputs = searchFacetInputs.filter(
            (item) => item.name === 'ProductType'
        )?.[0]?.values
        const selectedFamilyInputs = searchFacetInputs.filter(
            (item) => item.name === 'SectionNames'
        )?.[0]?.values
        //get selected product type name in workspace

        let updatedSearchFacetInputs = [...searchFacetInputs]

        // In case of Project, we have to pass specAgentProductTypeId of ProductTypes
        if (nodeId && projectId) {
            updatedSearchFacetInputs = replaceSpecAgentIdForProductTypes(
                updatedSearchFacetInputs,
                productTypeFacets
            )
        }

        // get Product Type Selections Data for comparing productTypeFacets and searchFacetInputs
        const productTypeSelectionsData = getProductTypeSelectionsData(
            productTypeFacets,
            searchFacetInputs
        )

        setManufacturerProductSearchInput((past) => ({
            ...past,
            orderBy: defaultListingOrder,
            projectId,

            facets: ptps?.some((ptp) => ptp.productTypeProperties?.length! > 0)
                ? [...updatedSearchFacetInputs]
                : updatedSearchFacetInputs,
            productTypeSelections: productTypeSelectionsData
                ? productTypeSelectionsData?.map((item, index) => {
                    const withManufacturerOptionFilter = selectedProductTypesWithManufacturerOption?.includes(
                        item.value?.toUpperCase()
                    )
                        ? { withManufacturerOption: true }
                        : {}

                    return {
                        sectionName: item.parentProductFamily,
                        productTypeName: item.value,
                        productTypeProperties: ptps?.find(
                            (ptp) =>
                                ptp?.sectionName ===
                                selectedFamilyInputs?.[index]
                        )?.productTypeProperties,
                        ...withManufacturerOptionFilter,
                    }
                })
                : ptps,
        }))
        //check if the ptp is changed
        if (
            !isEqual(
                productTypeSelections?.flatMap(
                    (x) => x.productTypeProperties ?? []
                ),
                propertyOptions
            )
        ) {
            setRefetch(true) // Removing this code affects the live refresh on product listing when ptp changes
        }
    }, [
        searchFacetInputs,
        propertyOptions,
        productTypeSelections,
        orderBy,
        selectedProductTypesWithManufacturerOption,
    ])

    const handleProductAddFlagForReview = React.useCallback(
        (listingId: string, isBOD: boolean) => {
            if (!!projectId) {
                // Flag the product for review
                setIsFlagForReviewInProgress(true)
                addFlagForReview({
                    variables: {
                        projectId: projectId,
                        listingId: listingId,
                        isFromRevit,
                    },
                }).then((value) => {
                    setIsFlagForReviewInProgress(false)

                    // Remove the product from the list only if already added
                    const addedListing = productSelected?.find(
                        (product) => product?.listingId === listingId
                    )
                    if (!!addedListing) {
                        handleRemoveProductOption(listingId, isBOD)
                    } else {
                        setProductListingFlaggedForReview(
                            value?.data?.createProductListingFlagForReview
                                ?.items || []
                        )
                    }
                })
            }
        },
        [
            projectId,
            addFlagForReview,
            setProductListingFlaggedForReview,
            setIsFlagForReviewInProgress,
            handleRemoveProductOption,
            productSelected,
        ]
    )

    const handleProductRemoveFlagForReview = React.useCallback(
        (listingId: string) => {
            if (!!projectId) {
                setIsFlagForReviewInProgress(true)
                removeFlagForReview({
                    variables: {
                        projectId: projectId,
                        listingId: listingId,
                    },
                }).then((value) => {
                    setProductListingFlaggedForReview(
                        value?.data
                            ?.removeProductListingFlagForReviewByListingId
                            ?.items || []
                    )
                    setIsFlagForReviewInProgress(false)
                    if (product[productOptions.FLAGGED_FOR_REVIEW]) {
                        setManufacturerProductSearchInput((past) => ({
                            ...past,
                            projectId,
                            listingIdsList: past.listingIdsList?.filter(
                                (each) => each !== listingId
                            ),
                        }))
                        setRefetch(true)
                    }
                })
            }
        },
        [
            projectId,
            removeFlagForReview,
            setProductListingFlaggedForReview,
            setIsFlagForReviewInProgress,
            product,
        ]
    )

    //refetch on filter change
    React.useEffect(() => {
        setRefetch(true)
    }, [searchFacetInputs])

    // on refetch change
    React.useEffect(() => {
        if (!searchLoading && refetch) {
            doRefetch()
        }
    }, [doRefetch, searchLoading, refetch])

    // on Render
    React.useEffect(() => {
        if (!searchCalled && hasMountedRef.current) {
            doRefetch()
        }
    }, [doRefetch, searchCalled])

    React.useEffect(() => {
        if (
            !searchLoading &&
            !getListingsLoading &&
            !!searchData?.listingSearch?.matches &&
            !!searchData?.listingSearch?.count
        ) {
            const count = searchData?.listingSearch?.matches?.length

            if (currentTotal !== count) {
                setCurrentTotal(count)
                setHasNextPage(
                    count >= 50
                        ? count < searchData?.listingSearch?.count
                        : false
                )
            }
        }
        if (
            !searchLoading &&
            !getrelatedProductsLoading &&
            !!relatedProducts?.listingSearch?.matches &&
            !!relatedProducts?.listingSearch?.count
        ) {
            const count = relatedProducts?.listingSearch?.matches?.length

            if (currentRTotal !== count) {
                setCurrentRTotal(count)
                setRHasNextPage(
                    count >= 50
                        ? count < relatedProducts?.listingSearch?.count
                        : false
                )
            }
        }
    }, [
        searchLoading,
        searchData,
        getListingsLoading,
        relatedProducts,
        getrelatedProductsLoading,
    ])

    React.useEffect(() => {
        if (
            !projectCalled &&
            !projectLoading &&
            projectId &&
            projectId !== '' &&
            projectId !== 'undefined'
        ) {
            getProject({
                variables: {
                    projectId: projectId,
                },
            })
        }
    }, [getProject, projectCalled, projectLoading, projectId])

    React.useEffect(() => {
        if (!autosuggestCardData) {
            if (nodeId && nodeId !== 'undefined') {
                if (!isAssembly) {
                    onGetProductTypesWithBod(
                        isDivisionSelected || !nodeId ? undefined : [nodeId]
                    )
                }
            }
            if (isGettingSelectedNuggetRequired) {
                getSelectedNugget({
                    variables: {
                        input: {
                            elementId,
                            projectId,
                        },
                        nodeId: nodeId,
                    },
                })
            }
        }
    }, [
        elementType,
        elementId,
        nodeId,
        projectId,
        isGettingSelectedNuggetRequired,
        getProductTypesWithBod,
        getSelectedNugget,
        isAssembly,
        isProductType,
        productTypeWithBODForDivisionInput,
        isDivisionSelected,
    ])

    // Read data from service worker
    React.useEffect(() => {
        if (!serviceWorker) return

        const handleServiceWorkerMessage = ({ data }: MessageEvent) => {
            // make sure that the data coming from SW is for this page.
            const workspaceProductListingState = data[
                WORKSPACE_PRODUCT_LISTING_KEY
            ] as WorkspaceProductListing
            if (workspaceProductListingState !== undefined) {
                // check if same project id is being serviced
                if (workspaceProductListingState.projectId !== projectId) return

                // Update with new values from workspace, fallback to current URL values
                const preservedState = {
                    ...workspaceProductListingState,
                    baseElementId:
                        workspaceProductListingState.baseElementId ||
                        baseElementId ||
                        '',
                    treePath:
                        workspaceProductListingState.treePath || treePath || '',
                }

                history.push(getDialogUrl(preservedState, 'dialog'))
                setCloseSlide(false)

                // Update filters when element changes from tree click
                setSearchFacetInputs((past) => {
                    let filtered = [
                        ...past.filter(
                            (each) =>
                                each.name !== 'ProductType' &&
                                each.name !== 'SectionNames' &&
                                each.name !== 'categories' &&
                                each.name !== 'assemblyNodeId'
                        ),
                    ]

                    // Add ProductType filter if present
                    if (
                        workspaceProductListingState.productType &&
                        workspaceProductListingState.productType !== '' &&
                        workspaceProductListingState.productType !== 'undefined'
                    ) {
                        filtered = [
                            ...filtered,
                            {
                                name: 'ProductType',
                                values: [workspaceProductListingState.productType],
                            },
                        ]
                    }

                    // Add Family filter if present
                    if (
                        workspaceProductListingState.family &&
                        workspaceProductListingState.family !== '' &&
                        workspaceProductListingState.family !== 'undefined'
                    ) {
                        filtered = [
                            ...filtered,
                            {
                                name: 'SectionNames',
                                values: [workspaceProductListingState.family],
                            },
                        ]
                    }

                    // Add category filter if present
                    if (
                        workspaceProductListingState?.category?.name &&
                        workspaceProductListingState.category.name !== 'All Categories'
                    ) {
                        filtered = [
                            ...filtered,
                            {
                                name: 'categories',
                                values: [workspaceProductListingState.category.name],
                            },
                        ]
                    }

                    // Add assemblyNodeId filter if it's an assembly
                    const isAssemblyElement =
                        workspaceProductListingState.elementType === 'ASSEMBLY' &&
                        !workspaceProductListingState.isLastSubAssembly
                    const isProductFamilyElement =
                        workspaceProductListingState.elementType === 'PRODUCTFAMILY'
                    const isProductTypeElement =
                        workspaceProductListingState.elementType === 'PRODUCTTYPE'

                    if (
                        !isProductFamilyElement &&
                        !isProductTypeElement &&
                        (isAssemblyElement || workspaceProductListingState.isLastSubAssembly) &&
                        workspaceProductListingState.nodeId
                    ) {
                        filtered = [
                            ...filtered,
                            {
                                name: 'assemblyNodeId',
                                values: [workspaceProductListingState.nodeId],
                            },
                        ]
                    }

                    return [...filtered]
                })

                if (
                    workspaceProductListingState?.updateBODList &&
                    workspaceProductListingState?.specAgentIdPt !==
                    specAgentIdPt &&
                    nodeId
                ) {
                    onGetProductTypesWithBod(
                        isDivisionSelected || !nodeId ? undefined : [nodeId]
                    )
                }
            }

            const workspaceManufacturerSelectedState = data[
                WORKSPACE_MANUFACTURER_SELECTED_KEY
            ] as WorkspaceManufacturerSelected

            if (workspaceManufacturerSelectedState !== undefined) {
                if (workspaceManufacturerSelectedState.removeSelected) {
                    setProductSelected([])
                    resetProductSearchQueryInput(
                        workspaceManufacturerSelectedState
                    )
                } else {
                    if (workspaceManufacturerSelectedState.isSelected) {
                        setProductSelected((past) => [
                            ...past.filter(
                                (each) =>
                                    each.listingId !==
                                    workspaceManufacturerSelectedState.listingId
                            ),
                            {
                                listingId:
                                    workspaceManufacturerSelectedState.listingId,
                                selectionType: workspaceManufacturerSelectedState.isBOD
                                    ? 'BOD'
                                    : 'Product',
                                isBOD:
                                    workspaceManufacturerSelectedState.isBOD ??
                                    false,
                                hasSupportedManufacturersOptions:
                                    workspaceManufacturerSelectedState.hasSupportedManufacturersOptions ??
                                    false,
                                elementId: workspaceManufacturerSelectedState.elementId as string,
                            },
                        ])
                        setManufacturerProductSearchInput((past) => ({
                            ...past,
                            projectId,
                            listingIdsList: past.listingIdsList?.filter(
                                (each) =>
                                    each !==
                                    workspaceManufacturerSelectedState.listingId
                            ),
                        }))
                    } else {
                        setProductSelected((past) => [
                            ...past.filter(
                                (each) =>
                                    each.listingId !==
                                    workspaceManufacturerSelectedState.listingId
                            ),
                        ])
                    }
                }

                if (workspaceManufacturerSelectedState.deleteBODListing) {
                    const matchedListing: ProductListingSelected = productSelected.filter(
                        (item) => item.isActive && item.selectionType === 'BOD'
                    )?.[0]
                    if (matchedListing && productTypeFacets) {
                        const {
                            familyElementId,
                        } = getProjectNuggetIdAndFamilyIdFromFacets(
                            productTypeFacets
                        )

                        deleteListingMutation({
                            isBOD: true,
                            listingId: matchedListing.listingId,
                            productFamilyId: familyElementId,
                            projectId,
                            projectNuggetId: matchedListing.elementId || undefined,
                            isRetainProperties: false, // TODO: Change value if there is removal warning
                            firmName: matchedListing?.firmName,
                            type: matchedListing?.type
                                ?.replace(/\s/g, '')
                                .toUpperCase(),
                        } as DeleteProjectManufacturerProductListingInput)
                        setProductSelected((past) => [
                            ...past.filter(
                                (each) =>
                                    each.listingId !== matchedListing.listingId
                            ),
                        ])
                    }

                    resetProductSearchQueryInput(
                        workspaceManufacturerSelectedState
                    )
                }
                if (!!product[productOptions.ADDED_TO_PROJECT]) {
                    setManufacturerProductSearchInput((past) => ({
                        ...past,
                        projectId,

                        listingIdsList: workspaceManufacturerSelectedState.isSelected
                            ? [
                                ...((past?.listingIdsList ?? []) as string[]),
                                workspaceManufacturerSelectedState.listingId,
                            ]
                            : past.listingIdsList?.filter(
                                (each) =>
                                    each !==
                                    workspaceManufacturerSelectedState.listingId
                            ),
                    }))
                    setCurrentTotal(OPTIMISTIC_CARDS)
                }
                // Get updated list for listings that are flagged for review
                getProductFlaggedForReviewProducts({
                    variables: {
                        projectId: projectId,
                    },
                })
                if (isGettingSelectedNuggetRequired) {
                    getSelectedNugget({
                        variables: {
                            input: {
                                elementId: isProductType ? elementId : '',
                                projectId,
                            },
                            nodeId: nodeId,
                        },
                    })
                }
                if (
                    !isAssembly &&
                    nodeId &&
                    typeof nodeId !== 'undefined' &&
                    nodeId !== 'undefined'
                ) {
                    onGetProductTypesWithBod(
                        isDivisionSelected || !nodeId ? undefined : [nodeId]
                    )
                }
                setRefetch(true)
            }

            const workspaceEnabledPTPOptions = data[
                WORKSPACE_ENABLED_PTP_OPTIONS
            ] as ProductTypePropertiesInputType[]

            if (
                workspaceEnabledPTPOptions &&
                !Object.values(product).some((value) => !!value)
            ) {
                // delay update to input since this will be updated many times in the workspace
                debounceUpdate(
                    workspaceEnabledPTPOptions.map((ptpOption) => ({
                        propertyName: ptpOption.propertyName.toString(),
                        propertyOptionIds: ptpOption.propertyOptionIds.map(
                            (property) => property.toString().normalize('NFKC')
                        ),
                    }))
                )
            }
            // check to see if BP tags are updated on the project workspace page
            const workspaceProductTypeManufacturerListingItems =
                data[WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY]
            if (!!workspaceProductTypeManufacturerListingItems) {
                handleUpdateWorkspaceManufacturerBP(
                    workspaceProductTypeManufacturerListingItems
                )
            }
        }

        serviceWorker?.addEventListener('message', handleServiceWorkerMessage)
        return () => {
            serviceWorker?.removeEventListener(
                'message',
                handleServiceWorkerMessage
            )
        }
    }, [
        serviceWorker,
        history,
        projectId,
        product,
        hasFamily,
        family,
        hasProductType,
        productType,
        productSelected,
        elementType,
        elementId,
        projectNuggetData,
        isGettingSelectedNuggetRequired,
        debounceUpdate,
        deleteListingMutation,
        getProductTypesWithBod,
        getSelectedNugget,
        isAssembly,
        isProductType,
        nodeId,
        isProductFamily,
        getProductTypeFacetFromListing,
        productTypeWithBODForDivisionInput,
        isDivisionSelected,
        getProductFlaggedForReviewProducts,
        getProjectNuggetIdAndFamilyIdFromFacets,
        productTypeFacets,
    ])

    React.useEffect(() => {
        window.addEventListener('beforeunload', unloadListener)

        return () => {
            window.removeEventListener('beforeunload', unloadListener)
        }
    }, [unloadListener])

    React.useEffect(() => {
        updateBrowserTitle?.(
            projectData?.getProject?.name
                ? `${projectData?.getProject?.name} - ${ProductListings}`
                : `${ProductListings}`
        )
    }, [projectData, updateBrowserTitle])

    React.useMemo(() => {
        if (projectId && !productsPage) {
            // set in localstorage if window is open
            updatePersistentConfigs(
                userId,
                projectId,
                'projectProductDialogOpened',
                true
            )
        }
    }, [projectId, userId, productsPage])

    React.useMemo(() => {
        const loadedIds =
            projectProductListingData?.getProjectManufacturerProductListing.map(
                (each) => each.listingId as string
            ) ?? []

        if (!projectProductListingLoading && wasListingsLoading) {
            if (!isEqual(productSelectedListingIds, loadedIds)) {
                setProductSelected(
                    projectProductListingData?.getProjectManufacturerProductListing.map(
                        (each) => ({
                            listingId: each.listingId as string,
                            selectionType: each.isBOD ? 'BOD' : 'Product',
                            isBOD: each.isBOD ?? false,
                            hasSupportedManufacturersOptions:
                                each.hasSupportedManufacturersOptions ?? false,
                            productType: each.productType,
                            elementId: each.elementId as string,
                        })
                    ) ?? []
                )
            }
        }
    }, [
        projectProductListingData,
        projectProductListingLoading,
        productSelectedListingIds,
        wasListingsLoading,
    ])

    React.useEffect(() => {
        if (
            currentUserInfoResponse &&
            JSON.stringify(userDetails) === JSON.stringify(userInfoInitial)
        ) {
            setUser(currentUserInfoResponse.currentUserInfo)
        }
    }, [userDetails, currentUserInfoResponse])

    if (!currentUserInfoResponse) return null

    return (
        <>
            <DialogProvider
                getFacetSelectedOptions={getFacetSelectedOptions}
                projectDetail={projectData?.getProject}
                hidePopper={
                    !!productsPage ||
                    // !!divisionNumber || NOTE: commented in lieu of #2413754
                    !!isAEAllProductsToggled
                }
                closeSlide={closeSlide}
                productSelected={productSelected}
                handleAddProductOption={handleAddProductOption}
                handleRemoveProductOption={onRemoveProductListing}
                handleModifyProductOption={handleModifyProductOption}
                selectedManufacturers={selectedManufacturers}
                handleToggleMultipleManufacturerSelections={
                    handleToggleMultipleManufacturerSelections
                }
                handleMultiSelectManufacturer={handleMultiSelectManufacturer}
                productListingFlaggedForReview={productListingFlaggedForReview}
                handleProductAddFlagForReview={handleProductAddFlagForReview}
                handleProductRemoveFlagForReview={
                    handleProductRemoveFlagForReview
                }
                handleUpdateWorkspaceManufacturerBP={
                    handleUpdateWorkspaceManufacturerBP
                }
                emptyElementTree={
                    assemblyPath === null ||
                    assemblyPath === undefined ||
                    assemblyPath?.trim()?.length === 0
                }
                userRoles={currentUserInfoResponse?.currentUserInfo.roles}
                selectedListingId={listingId as string}
                projectProductListings={
                    isProductType && !projectNUggetLoading
                        ? projectNuggetData?.getProjectNugget.productListings
                        : undefined
                }
                isAEAllProductsToggled={isAEAllProductsToggled}
                isProductsPage={productsPage}
                setHeaderPillToggle={setHeaderPillToggle}  >
                <DialogAppBar name="Product Listings" />
                <DialogContent
                    isBannerExpanded={isBannerExpanded}
                    slideContent={
                        <SlideContent
                            searchLoading={
                                searchLoading || searchFetchMoreLoading
                            }
                            projectId={projectId}
                        />
                    }
                >
                    <DialogFilterProvider
                        getFacetOptions={getFacetOptions}
                        getProductFacetOptions={getProductFacetOptions}
                        handleFacetSelectedChange={handleFacetSelectedChange}
                        removeProductType={removeProductType}
                        isProductTypeFacetsLoading={isProductTypeFacetsLoading}
                    >
                        <DialogHeader
                            category={getBreadcrumbsWithCategory()}
                            productTypeFacets={productTypeFacets}
                            handleAutocompleteNameChange={
                                handleAutocompleteNameChange
                            }
                            handleAutocompleteInputValueChange={
                                handleAutocompleteInputValueChange
                            }
                            autosuggestLoading={
                                autosuggestLoading ||
                                getListingsLoading ||
                                searchFetchMoreLoading ||
                                searchLoading ||
                                autosuggestCardLoading
                            }
                            queryParams={queryParams}
                        />
                        {dialogTab === 0 ? (
                            <AdvancedFilters
                                listingType={listingType}
                                listingTypeOptions={
                                    productsPage
                                        ? defaultFilter
                                        : [...defaultFilter, 'Manufacturer']
                                }
                                handleListingType={handleListingType}
                                product={product}
                                productOptions={Object.values(productOptions)}
                                handleProduct={handleProduct}
                                handleAutocompleteNameChange={
                                    handleAutocompleteNameChange
                                }
                                autosuggestLoading={
                                    autosuggestLoading ||
                                    getListingsLoading ||
                                    searchFetchMoreLoading ||
                                    searchLoading ||
                                    autosuggestCardLoading
                                }
                                handlePartnershipSelection={
                                    handlePartershipSelection
                                }
                                bimResources={bimResources}
                                sustainabilityResources={sustainabiliyResources}
                                sustainabilityResourcesOptions={
                                    sustainabilityResourcesOptions
                                }
                                bimResourcesOptions={bimResourcesOptions}
                                queryParams={queryParams}
                                selectedTab={dialogTab}
                                specialty={specialty}
                                handleSpecialtyClick={handleSpecialtyClick}
                                specialtyOptions={['Manufacturer Options']}
                                handleClearAllClick={handleClearAllClick}
                            />
                        ) : (
                            <GridContainer
                                style={{
                                    display: 'flex',
                                    minHeight: '5.35rem',
                                    backgroundColor: listingDialogFilterContainerColor,
                                    borderTop: `1px solid ${advancedFilterHRColor}`,
                                    borderBottom: `1px solid ${advancedFilterHRColor}`,
                                }}
                            />
                        )}
                    </DialogFilterProvider>
                    <DialogTabs
                        listingProps={{
                            searchLoading: searchLoading,
                            searchFetchMoreLoading: searchFetchMoreLoading,
                            handleLoadMore: handleLoadMore,
                            products: isEmptyListingDueToFilter
                                ? [] // Show no Results
                                : searchData?.listingSearch?.matches ?? [],
                            currentTotal: currentTotal,
                            hasNextPage: hasNextPage,
                            isItemLoaded: isItemLoaded,
                            scrollState: scrollState,
                            setScrollRowAndColumn: setScrollRowAndColumn,
                            infiniteLoaderRef: infiniteLoaderRef,
                            setColumnCount: setColumnCount,
                            isAssembly: isAssembly,
                            productTypesWithBOD: productTypesWithBOD,
                        }}
                        rListingProps={{
                            products: isEmptyListingDueToFilter
                                ? [] // Show no Results
                                : relatedProducts?.listingSearch?.matches ?? [],
                            currentTotal: currentRTotal,
                            hasNextPage: rHasNextPage,
                        }}
                        setDialogTab={setDialogTab}
                        relatedProductsCount={
                            isEmptyListingDueToFilter
                                ? 0
                                : relatedProducts?.listingSearch?.count
                        }
                        productsCount={searchData?.listingSearch?.count}
                        isRelatedProductsActive={dialogTab === 1}
                    />
                </DialogContent>
                {userId !== undefined &&
                    processEnv.REACT_APP_EnablePPLFeature !== '1' && (
                        <RelatedProductsBanner
                            category={category ?? ''}
                            setDialogTab={setDialogTab}
                            activeDialogTab={dialogTab}
                            updateDialogContainer={updateIsBannerExpanded}
                            userId={userId}
                        />
                    )}
                <Backdrop
                    className={classes.backdrop}
                    open={isAddElementsLoading || isFlagForReviewInProgress}
                >
                    <CircularProgress
                        color="inherit"
                        data-testid="addSpinner"
                    />
                </Backdrop>
            </DialogProvider>
            {isRemoveWarningOpen && (
                <RemoveProductWarning
                    isDisplayed={isRemoveWarningOpen}
                    onCancel={closeWarningDialog}
                    onRemoveProductListing={triggerRemoveProductListing}
                />
            )}
        </>
    )
}

export default ProductListingDialog
